import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import logo from "../assets/logo.png";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import EditIcon from "@mui/icons-material/Edit";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Grid2,
  Box,
  Slider,
  Typography,
  Button,
  TextField,
  IconButton,
  DialogContent,
  DialogTitle,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import SignatureModalContent from "../components/SignatureModalContent";
import { format, parse, isValid } from "date-fns";
import FileUpload from "../components/FileUpload";
import "../styles/App.styles.css";
import "../App.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import ImageCropperModal from "../components/ImageCropperModal";
import SignersDialog from "../components/SignersDialog";
import {
  downloadOriginalPdf,
  downloadPdfWithTextAnnotations,
  getColorWithNewOpacity,
  getSignerBoxContent,
  hashIds,
  unhashIds,
} from "../components/utlis";
import {
  getUserLocation,
  post,
  get,
  postFormData,
  termsConditions,
  websiteBaseUrl,
  link_base_url,
} from "../apis/apis";
import { applyRegionFromSigningToken } from "../utility/region/linkCrypto";

import DropdownPropertiesPanel from "../components/DropdownPropertiesPanel";
import toastAlert from "../components/toastAlert";
import getActivityLogUserFiles from "../components/getActivityLogUserFiles";
import { useParams, useLocation } from "react-router-dom";
import AccessCodeDialog from "../components/AccessCodeDialog";
import ErrorModal from "../components/ErrorModal";
import WaitForOthersDialog from "../components/WaitForOthersDialog";
import CompletionModal from "../components/CompletionModal";
import logoRemoveBg from "../assets/logo.png";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import TextEntryDialog from "../components/TextEntryDialog";
import ResidencyDisclosure from "../components/ResidencyDisclosure";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

function EsignReceivingEnd() {
  const getSignerColorById = (signerId, signers) => {
    const signer = signers.find(
      (s) => s.signer_id === signerId || s.id === signerId
    );
    return signer?.color || "rgba(0, 0, 0, 0.1)"; // Default color if signer not found
  };
  const { emailHashed, file_id, senderId, token, encryptedFileId } = useParams();
  const location = useLocation();

  // Check if KBA is required from URL path
  // URL format: 
  // With KBA: /send-doc-to-esign/{email}/{id}/{userId}/{token}/{fileId}/kba_required
  // Without KBA: /send-doc-to-esign/{email}/{id}/{userId}/{token}/{fileId}
  const isKbaRequired = location.pathname.endsWith('/kba_required');

  // Log for debugging
  useEffect(() => {
    console.log('=== KBA Detection ===');
    console.log('Pathname:', location.pathname);
    console.log('Is KBA Required:', isKbaRequired);
  }, [location.pathname, isKbaRequired]);

  // Function to start KBA verification
  const startKbaVerification = async () => {
    if (!isKbaRequired || !kbaFullName || !kbaEmail || !kbaDateOfBirth) {
      toastAlert("error", "Please fill in all KBA fields");
      return false;
    }

    // Validate that fullName contains at least first and last name
    const nameParts = kbaFullName.trim().split(/\s+/).filter(part => part.length > 0);
    if (nameParts.length < 2) {
      toastAlert("error", "Please enter both first and last name");
      return false;
    }

    setIsLoadingKba(true);
    try {
      // Split fullName into firstName and lastName
      // First word is firstName, remaining words are lastName
      const firstName = nameParts[0].trim();
      const lastName = nameParts.slice(1).join(" ").trim();

      // Validate that we have both names after splitting
      if (!firstName || !lastName) {
        toastAlert("error", "Please enter both first and last name");
        setIsLoadingKba(false);
        return false;
      }

      const postData = {
        email: kbaEmail.trim(),
        firstName: firstName,
        lastName: lastName,
        dateOfBirth: kbaDateOfBirth,
        // Also send fullName as fallback
        fullName: kbaFullName.trim(),
      };

      console.log("Sending KBA data:", {
        email: postData.email,
        firstName: postData.firstName,
        lastName: postData.lastName,
        dateOfBirth: postData.dateOfBirth,
      });

      console.log("=== STARTING KBA VERIFICATION ===");
      console.log("Request data:", postData);

      const apiData = await post("api/kba/start", postData);

      console.log("=== KBA API RESPONSE ===");
      console.log("Full response:", apiData);
      console.log("Has error:", apiData.error);
      console.log("Has data:", !!apiData.data);
      console.log("verifyUrl:", apiData.data?.verifyUrl);
      console.log("sessionId:", apiData.data?.sessionId);

      if (apiData.error) {
        console.error("KBA API error:", apiData);
        toastAlert("error", apiData.message || "Failed to start KBA verification");
        setIsLoadingKba(false);
        return false;
      }

      // Set the verification URL and session ID
      if (apiData.data?.verifyUrl) {
        const verifyUrl = apiData.data.verifyUrl;
        const sessionId = apiData.data.sessionId || "";

        console.log("Setting KBA verify URL:", verifyUrl);
        console.log("Setting KBA session ID:", sessionId);

        setKbaVerifyUrl(verifyUrl);
        setKbaSessionId(sessionId);

        // Store context in localStorage for callback page
        localStorage.setItem("kba_session_id", sessionId);
        localStorage.setItem("kba_original_url", window.location.href);
        localStorage.setItem("kba_email", kbaEmail.trim());

        // Close welcome modal
        setWelcomeModal(false);
        setIsLoadingKba(false);

        // Note: verify.authenticating.com blocks iframe embedding via X-Frame-Options
        // This causes the browser to open it in a new tab instead of the iframe.
        // Since we can't override this, we'll open directly in a new window.

        // Open KBA verification in a new window (iframe is blocked by external site)
        const newWindow = window.open(verifyUrl, '_blank', 'noopener,noreferrer');

        if (newWindow) {
          console.log("KBA verification opened in new window:", verifyUrl);
          toastAlert("info", "KBA verification opened in a new window. Please complete verification there.");

          // Monitor the new window to check when verification completes
          // We'll check the status periodically or wait for user to return
        } else {
          // Pop-up blocked - show modal with iframe as fallback
          console.log("Pop-up blocked, opening modal with iframe");
          setTimeout(() => {
            setKbaModalOpen(true);
            console.log("KBA modal opened with URL:", verifyUrl);
          }, 100);
        }

        return true;
      } else {
        console.error("No verifyUrl in response:", apiData);
        toastAlert("error", apiData.message || "Failed to get verification URL from server");
        setIsLoadingKba(false);
        return false;
      }
    } catch (error) {
      console.error("Error starting KBA verification:", error);
      toastAlert("error", "Failed to start KBA verification");
      setIsLoadingKba(false);
      return false;
    }
  };

  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Unified mobile check (md = 900px, close to 780)

  // #region agent log
  useEffect(() => {
    try {
      fetch('http://127.0.0.1:7246/ingest/8b376689-945a-4a97-94d5-d387ea1b8602', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: 'debug-session', runId: 'placement-pre', hypothesisId: 'P0', location: 'src/pages/EsignReceivingEndCopy.js:mount', message: 'debug ping', data: { isMobile: !!isMobile, vw: window?.innerWidth, vh: window?.innerHeight }, timestamp: Date.now() }) }).catch(() => { });
    } catch (_) { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // #endregion

  // Detect mobile orientation (both vertical and horizontal)
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  // Track window dimensions for real-time Chrome compatibility
  const [windowDimensions, setWindowDimensions] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  }));

  // Computed landscape value (updates when state changes)
  const effectiveIsMobileLandscape = useMemo(() => {
    if (!isMobile) return false;

    // Use state first
    if (isMobileLandscape) return true;

    // Use windowDimensions state for reactive updates
    const width = windowDimensions.width;
    const height = windowDimensions.height;

    // Check if landscape based on dimensions
    return width > height && width >= 600;
  }, [isMobile, isMobileLandscape, windowDimensions]);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileLandscape(false);
      setIsMobilePortrait(false);
      return;
    }

    const checkOrientation = () => {
      // Use multiple methods for better cross-browser compatibility
      // Method 1: Media query (works in most browsers)
      const mediaQueryLandscape = window.matchMedia('(orientation: landscape)');
      const mediaQueryPortrait = window.matchMedia('(orientation: portrait)');

      // Method 2: Window dimensions (fallback for Chrome/Android)
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const dimensionBasedLandscape = windowWidth > windowHeight;

      // For Chrome: Use dimension-based check as primary, media query as secondary
      // Chrome's media query can be unreliable, so prioritize dimensions
      const isChrome = /Chrome/.test(navigator.userAgent) && !/Edg/.test(navigator.userAgent);
      let isLandscape, isPortrait;

      if (isChrome) {
        // Chrome: Prioritize dimension-based check
        isLandscape = dimensionBasedLandscape && windowWidth >= 600;
        isPortrait = !isLandscape;
      } else {
        // Other browsers: Use media query first, dimensions as fallback
        isLandscape = mediaQueryLandscape.matches || (dimensionBasedLandscape && windowWidth >= 600);
        isPortrait = mediaQueryPortrait.matches || (!dimensionBasedLandscape || windowWidth < 600);
      }

      setIsMobileLandscape(isLandscape);
      setIsMobilePortrait(isPortrait);

      // Debug logging for orientation detection
      console.log('Orientation check:', {
        isChrome,
        mediaQueryLandscape: mediaQueryLandscape.matches,
        mediaQueryPortrait: mediaQueryPortrait.matches,
        dimensionBasedLandscape,
        windowWidth,
        windowHeight,
        finalIsLandscape: isLandscape,
        finalIsPortrait: isPortrait,
        userAgent: navigator.userAgent
      });
    };

    // Check on mount with a small delay to ensure window dimensions are accurate
    setTimeout(checkOrientation, 0);
    setTimeout(checkOrientation, 100);
    setTimeout(checkOrientation, 300);

    // Listen for orientation changes
    const mediaQueryLandscape = window.matchMedia('(orientation: landscape)');
    const mediaQueryPortrait = window.matchMedia('(orientation: portrait)');

    const handleOrientationChange = () => {
      // Add delay to ensure dimensions are updated
      setTimeout(checkOrientation, 0);
      setTimeout(checkOrientation, 100);
    };

    // Modern browsers
    if (mediaQueryLandscape.addEventListener) {
      mediaQueryLandscape.addEventListener('change', handleOrientationChange);
      mediaQueryPortrait.addEventListener('change', handleOrientationChange);
    } else {
      // Fallback for older browsers
      mediaQueryLandscape.addListener(handleOrientationChange);
      mediaQueryPortrait.addListener(handleOrientationChange);
    }

    // Also listen to window resize as fallback (important for Chrome)
    // Use debounced resize handler for Chrome
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        checkOrientation();
        // Double-check after a short delay for Chrome
        setTimeout(checkOrientation, 50);
      }, 50);
    };
    window.addEventListener('resize', handleResize);

    // Listen to orientationchange event (works in Safari and Chrome)
    window.addEventListener('orientationchange', () => {
      // Immediate check
      checkOrientation();
      // Multiple delayed checks for Chrome
      setTimeout(checkOrientation, 0);
      setTimeout(checkOrientation, 100);
      setTimeout(checkOrientation, 300);
      setTimeout(checkOrientation, 500);
    });

    // Additional Chrome-specific: Use visual viewport API if available
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        setTimeout(checkOrientation, 0);
        setTimeout(checkOrientation, 100);
      });
    }

    return () => {
      if (mediaQueryLandscape.removeEventListener) {
        mediaQueryLandscape.removeEventListener('change', handleOrientationChange);
        mediaQueryPortrait.removeEventListener('change', handleOrientationChange);
      } else {
        mediaQueryLandscape.removeListener(handleOrientationChange);
        mediaQueryPortrait.removeListener(handleOrientationChange);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleOrientationChange);
      }
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [isMobile]);

  // Track window dimensions for Chrome compatibility
  useEffect(() => {
    const updateDimensions = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;

      console.log('[Chrome Debug] Updating dimensions:', { newWidth, newHeight, isLandscape: newWidth > newHeight });

      setWindowDimensions(prev => {
        // Only update if dimensions actually changed
        if (prev.width !== newWidth || prev.height !== newHeight) {
          return { width: newWidth, height: newHeight };
        }
        return prev;
      });

      // Also update the orientation states directly for Chrome
      const isChrome = /Chrome/.test(navigator.userAgent) && !/Edg/.test(navigator.userAgent);
      if (isChrome && isMobile) {
        const isLandscape = newWidth > newHeight && newWidth >= 600;
        setIsMobileLandscape(isLandscape);
        setIsMobilePortrait(!isLandscape);
        console.log('[Chrome Debug] Set orientation:', { isLandscape, isPortrait: !isLandscape });
      }
    };

    // Update immediately
    updateDimensions();
    // Update again after a short delay (for Chrome)
    setTimeout(updateDimensions, 50);
    setTimeout(updateDimensions, 200);

    // Update on resize (frequent updates for Chrome)
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      // Immediate update
      updateDimensions();
      // Delayed update to catch Chrome's delayed dimension changes
      resizeTimer = setTimeout(updateDimensions, 100);
    };
    window.addEventListener('resize', handleResize);

    // Also update on orientation change
    const handleOrientationChange = () => {
      // Multiple updates to catch Chrome's delayed changes
      updateDimensions();
      setTimeout(updateDimensions, 0);
      setTimeout(updateDimensions, 50);
      setTimeout(updateDimensions, 100);
      setTimeout(updateDimensions, 300);
      setTimeout(updateDimensions, 500);
    };
    window.addEventListener('orientationchange', handleOrientationChange);

    // Also track visual viewport for Chrome mobile
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateDimensions);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateDimensions);
      }
      clearTimeout(resizeTimer);
    };
  }, [isMobile]);

  const [compLogo, setCompLogo] = useState("");
  const [compPrimaryColor, setCompPrimaryColor] = useState("");
  // Add new state at the top of App component
  const [signerToEdit, setSignerToEdit] = useState(null);
  const [pageNoClicked, setPageNoClicked] = useState(null);
  const styles = {
    sidebarStyle: {
      cursor: "default !important",
      "& *": {
        cursor: "default !important",
      },
      "& button": {
        cursor: "pointer !important",
      },
      "& .thumbnail-container": {
        cursor: "pointer !important",
      },
    },
  };
  const isSignatureType = (type) =>
    ["signer_initials", "signer_initials_text"].includes(type);
  // Add this helper function to determine if it's an image upload type
  const isImageUploadType = (type) =>
    [
      "signer_chooseImgStamp",
      "signer_chooseImgDrivingL",
      "signer_chooseImgPassportPhoto",
    ].includes(type);

  const colortheme = "rgb(35, 179, 232)";
  const [pdfFile, setPdfFile] = useState(null);
  const [scale, setScale] = useState(1); // Initial scale - will be recalculated on load to fit container
  const [isZoomed, setIsZoomed] = useState(false); // Track if PDF is zoomed for mobile
  const [pdfZoomScale, setPdfZoomScale] = useState(1); // Track pinch/zoom scale for PDF
  const [pdfImages, setPdfImages] = useState([]);
  const [containerWidth, setContainerWidth] = useState(0);
  const [activePage, setActivePage] = useState(1); // Track active page
  const [imageResizeKey, setImageResizeKey] = useState(0); // Force re-render when image resizes
  const [firstPageHeight, setFirstPageHeight] = useState(null);
  // At the top with other state declarations
  const [annotationType, setAnnotationType] = useState(null); // 'text' or 'image'
  // Modify the annotations state to store per-page annotations
  const [annotations, setAnnotations] = useState({}); // Changed from array to object with page keys
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [focusedAnnotation, setFocusedAnnotation] = useState(null);
  const [active, setActive] = useState("1");
  const [selectedSigner, setSelectedSigner] = useState([]);
  // First, add a state for storing click position
  const [stampClickPosition, setStampClickPosition] = useState(null);
  // Add this state in your App component
  const [signersDialogOpen, setSignersDialogOpen] = useState(false);
  const [signers, setSigners] = useState([]);
  // Inside your App.js, add this state
  const [selectedSignerId, setSelectedSignerId] = useState(null);
  // Add these helper functions
  const [pageSizes, setPageSizes] = useState({});

  // Add these states in your App component
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);

  const [initialsBox, setInitialBox] = useState(false);
  const pdfContainerRef = useRef(null);
  const sidebarRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  // Add this component inside your EsignReceivingEnd component
  const FullScreenLoader = () => (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(3, 3, 3, 0.8)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 25000, // Ensure loader appears above everything including modals (20000) and text fields (10000+)
      }}
    >
      <CircularProgress
        size={60}
        thickness={4}
        sx={{
          color: "rgb(35, 179, 232)",
          marginBottom: 2,
        }}
      />
      <Typography variant="h6" color="textSecondary" style={{ color: "white" }}>
        Loading Document...
      </Typography>
    </Box>
  );

  // Add new state at the top of your App component
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const annotationRefs = useRef({}); // Store refs for each annotation
  const cursorRef = useRef(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [activeAnnotationId, setActiveAnnotationId] = useState(null);

  const textAnnotationTypes = ["my_text", "signer_text", "date"];
  // states
  const [fileName, setFileName] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [securedShare, setSecuredShare] = useState(false);
  const [statusFile, setStatusFile] = useState("");
  const [uniqIdDoc, setUniqIdDoc] = useState("");
  const [fileUserid, setFileUserid] = useState("");
  const [docSignerStatusModal, setDocSignerStatusModal] = useState(false);
  const [all_recipientsData, setAll_RecipientsData] = useState([]);
  const [SignersWhoHaveCompletedSigning, setSignersWhoHaveCompletedSigning] =
    useState([]);
  const [errorModal, setErrorModal] = useState(false);
  const [welcomeModal, setWelcomeModal] = useState(true);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // KBA fields state
  const [kbaFullName, setKbaFullName] = useState("");
  const [kbaEmail, setKbaEmail] = useState("");
  const [kbaDateOfBirth, setKbaDateOfBirth] = useState("");

  // KBA verification modal state
  const [kbaModalOpen, setKbaModalOpen] = useState(false);
  const [kbaVerifyUrl, setKbaVerifyUrl] = useState("");
  const [kbaSessionId, setKbaSessionId] = useState("");
  const [isLoadingKba, setIsLoadingKba] = useState(false);
  const [iframeLoadAttempted, setIframeLoadAttempted] = useState(false);
  const [kbaVerified, setKbaVerified] = useState(false);
  const [kbaVerificationStatus, setKbaVerificationStatus] = useState(null); // 'pending' | 'passed' | 'failed'

  const [DocSignerStatus, setDocSignerStatus] = useState(false);
  const [waitforOtherusers, setWaitforOtherusers] = useState(false);
  const [waitOtherModal, setWaitOtherModal] = useState(false);

  const [activeSignerId, setActiveSignerId] = useState("");
  const stampInputRef = useRef(null);
  // Add these states at the top of your App component
  const [cropperModalOpen, setCropperModalOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [pendingStampAction, setPendingStampAction] = useState(null);
  // IMPORTANT: keep the latest intent (edit vs new) synced even if user selects
  // a file before React state updates have flushed.
  const pendingStampActionRef = useRef(null);

  const handleStampFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Prefer the ref (set right before opening the picker) to avoid first-click races.
    const refAction = pendingStampActionRef.current;
    const stateEditAction = editingAnnotation
      ? { isEdit: true, editData: editingAnnotation }
      : null;
    const defaultNewStampPosition = (() => {
      const pos = stampClickPosition;
      if (pos && typeof pos.x === "number" && typeof pos.y === "number") return pos;
      return { x: 50, y: 50 }; // fallback center (percent)
    })();

    const effectiveAction =
      refAction ||
      stateEditAction || {
        isEdit: false,
        newStampPosition: defaultNewStampPosition,
      };

    // #region agent log
    try {
      fetch('http://127.0.0.1:7246/ingest/8b376689-945a-4a97-94d5-d387ea1b8602', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: 'debug-session', runId: 'placement-pre', hypothesisId: 'P4', location: 'src/pages/EsignReceivingEndCopy.js:handleStampFileSelect', message: 'stamp file selected', data: { isMobile: !!isMobile, activePage, pageNoClicked, stampClickPosition, refAction: refAction ? { isEdit: !!refAction.isEdit, hasNewPos: !!refAction.newStampPosition, editPageNum: refAction.editData?.pageNum } : null, stateEditAction: stateEditAction ? { isEdit: !!stateEditAction.isEdit, editPageNum: stateEditAction.editData?.pageNum } : null, effectiveAction: effectiveAction ? { isEdit: !!effectiveAction.isEdit, newPos: effectiveAction.newStampPosition || null, editPageNum: effectiveAction.editData?.pageNum } : null }, timestamp: Date.now() }) }).catch(() => { });
    } catch (_) { }
    // #endregion

    const reader = new FileReader();
    reader.onload = (e) => {
      setCropSrc(e.target.result);
      setPendingStampAction(effectiveAction);
      setCropperModalOpen(true);
    };
    reader.readAsDataURL(file);
    // Reset input value to allow selecting the same file again (important for iOS)
    event.target.value = "";
  };
  // Add handler for cropped image
  const handleCroppedStamp = (croppedImage) => {
    const img = new Image();
    img.onload = () => {
      const defaultWidth = 150;
      const aspectRatio = img.width / img.height;
      const defaultHeight = defaultWidth / aspectRatio;

      if (!pendingStampAction) {
        // Nothing to apply; just clean up.
        setCropperModalOpen(false);
        setCropSrc(null);
        setPendingStampAction(null);
        pendingStampActionRef.current = null;
        setStampClickPosition(null);
        return;
      }

      if (pendingStampAction.isEdit) {
        // Handle editing existing stamp
        setAnnotations((prev) => {
          const updatedAnnotations = { ...prev };
          const { pageNum, index } = pendingStampAction.editData;

          const existingAnnot = updatedAnnotations[pageNum][index];
          updatedAnnotations[pageNum][index] = {
            ...existingAnnot,
            imageUrl: croppedImage,
            // height: existingAnnot.width / aspectRatio,
          };

          return updatedAnnotations;
        });
        setEditingAnnotation(null);
      } else {
        // Handle new stamp creation
        const pos = pendingStampAction?.newStampPosition;
        const safePos =
          pos && typeof pos.x === "number" && typeof pos.y === "number"
            ? pos
            : { x: 50, y: 50 };
        const resolvedPageNum = pageNoClicked || activePage;
        const newAnnotation = {
          id: Date.now(),
          x: safePos.x,
          y: safePos.y,
          type: "stamp",
          page_no: resolvedPageNum,
          imageUrl: croppedImage,
          // width: defaultWidth / scale,
          // height: defaultHeight / scale,
        };

        // #region agent log
        try {
          fetch('http://127.0.0.1:7246/ingest/8b376689-945a-4a97-94d5-d387ea1b8602', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: 'debug-session', runId: 'placement-pre', hypothesisId: 'P5', location: 'src/pages/EsignReceivingEndCopy.js:handleCroppedStamp(new)', message: 'creating new stamp annotation', data: { isMobile: !!isMobile, activePage, pageNoClicked, safePos, newAnnot: { x: newAnnotation.x, y: newAnnotation.y, type: newAnnotation.type, page_no: newAnnotation.page_no } }, timestamp: Date.now() }) }).catch(() => { });
        } catch (_) { }
        // #endregion

        setAnnotations((prev) => {
          const updatedAnnotations = {
            ...prev,
            [resolvedPageNum]: [...(prev[resolvedPageNum] || []), newAnnotation],
          };

          const newIndex = updatedAnnotations[resolvedPageNum].length - 1;
          setActiveAnnotation(`${resolvedPageNum}-${newIndex}`);

          return updatedAnnotations;
        });
      }

      // Reset states
      setCropperModalOpen(false);
      setCropSrc(null);
      setPendingStampAction(null);
      pendingStampActionRef.current = null;
      setStampClickPosition(null);
    };
    img.src = croppedImage;
  };

  // const calculateImageWidth = useCallback((scaleValue) => {
  //   const baseWidth = 100;

  //   // Simple screen size detection
  //   const isLargeScreen = window.innerWidth >= 1920; // 4K, ultrawide monitors
  //   const isLaptop = window.innerWidth >= 1440 && window.innerWidth < 1920;

  //   let effectiveScale = scaleValue;

  //   if (isLargeScreen) {
  //     // For large screens, cap the scale to prevent oversized PDFs
  //     // effectiveScale = Math.min(scaleValue, 1.0);
  //     effectiveScale = 0.9;
  //   } else if (isLaptop) {
  //     // For laptop screens, use scale as intended
  //     effectiveScale = scaleValue;
  //   }

  //   const scaledWidth = baseWidth * effectiveScale;
  //   return Math.max(scaledWidth, 50);
  // }, []);
  const calculateImageWidth = useCallback((scaleValue) => {
    // Use a fixed maximum width approach instead of viewport-dependent scaling
    const containerWidth =
      pdfContainerRef.current?.offsetWidth || window.innerWidth * 0.8;
    const maxPdfWidth = containerWidth * 0.95; // 95% of container width

    // Base PDF width should be consistent
    const basePdfWidth = Math.min(800, maxPdfWidth); // Max 800px base width

    // Apply scale factor
    const scaledWidth = basePdfWidth * scaleValue;

    // Convert to percentage of container
    return Math.min(95, (scaledWidth / containerWidth) * 100);
  }, []);
  // Memoize container width update
  const updateContainerWidth = useCallback(() => {
    if (pdfContainerRef.current) {
      setContainerWidth(pdfContainerRef.current.offsetWidth);
    }
  }, []);
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // check

  useEffect(() => {
    const pdfContainer = pdfContainerRef.current;
    if (!pdfContainer) return;

    const handleScroll = debounce((e) => {
      const container = e.target;
      const containerRect = container.getBoundingClientRect();
      const elements = document.querySelectorAll(".pdf-page-container");

      let detectedPage = null;

      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const isVisible =
          rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

        if (isVisible && !detectedPage) {
          detectedPage = parseInt(element.id.split("-")[1]);
        }
      });

      if (detectedPage) {
        setActivePage(detectedPage);
      }
    }, 16); // 60fps debounce

    pdfContainer.addEventListener("scroll", handleScroll);
    return () => pdfContainer.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollThumbnailIntoView = (pageNum) => {
    const thumbnailElement = document.getElementById(`thumbnail-${pageNum}`);

    if (thumbnailElement && sidebarRef.current) {
      thumbnailElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  };
  useEffect(() => {
    // if (sidebarVisible) {
    scrollThumbnailIntoView(activePage);
    // }
  }, [activePage]);
  useEffect(() => {
    const updatePageSizes = () => {
      setPageSizes((prev) => {
        const next = { ...(prev || {}) };
        pdfImages.forEach((_, pageIndex) => {
          const pageNum = pageIndex + 1;
          const imageElem = document.querySelector(`#img-${pageNum} .pdf-image`);
          const w = imageElem?.clientWidth || imageElem?.offsetWidth || 0;
          const h = imageElem?.clientHeight || imageElem?.offsetHeight || 0;
          // Don’t overwrite last-good values with transient zeros (happens in DevTools/mobile).
          if (w > 0 && h > 0) {
            next[pageNum] = { width: w, height: h };
          }
        });
        return next;
      });
    };

    updatePageSizes();

    window.addEventListener("resize", updatePageSizes);
    return () => window.removeEventListener("resize", updatePageSizes);
  }, [pdfImages, scale, isMobile]);
  const handlePageHover = (pageNum) => {
    setActivePage(pageNum); // Nothing else!
  };

  // Combine container width and resize effects
  useEffect(() => {
    updateContainerWidth();
    window.addEventListener("resize", updateContainerWidth);
    return () => window.removeEventListener("resize", updateContainerWidth);
  }, [updateContainerWidth, scale]);

  // Initialize zoom state for mobile view and handle image resize after scale change
  useEffect(() => {
    if (isMobile) {
      setIsZoomed(scale > 1.1 || pdfZoomScale > 1.1);
    }

    // Use ResizeObserver to detect when images resize after scale change
    // This ensures renderAnnotations runs with correct dimensions
    if (pdfImages.length > 0) {
      const resizeObservers = [];
      pdfImages.forEach((_, index) => {
        const pageNum = index + 1;
        const imageElem = document.querySelector(`#img-${pageNum} .pdf-image`);
        if (imageElem) {
          const observer = new ResizeObserver(() => {
            // Capture last-known good size for fallback paths (especially important on mobile,
            // where we can briefly observe height=0 during orientation / DevTools changes).
            const w = imageElem.clientWidth || imageElem.offsetWidth || 0;
            const h = imageElem.clientHeight || imageElem.offsetHeight || 0;
            if (w > 0 && h > 0) {
              setPageSizes((prev) => ({ ...(prev || {}), [pageNum]: { width: w, height: h } }));
            }
            // Force re-render of annotations when image resizes
            setImageResizeKey((prev) => prev + 1);
          });
          observer.observe(imageElem);
          resizeObservers.push(observer);
        }
      });

      return () => {
        resizeObservers.forEach((observer) => observer.disconnect());
      };
    }
  }, [scale, pdfImages, isMobile, pdfZoomScale]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedAnnotation(null);
        setAnnotationType(null);
        setIsAnnotating(false);
      }
    };

    // Add event listener when the component mounts
    document.body.addEventListener("keydown", handleKeyDown);

    // Remove event listener when the component unmounts
    return () => {
      document.body.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  const loadPdfFromUrl = async (url, token = null) => {
    console.log("=== LOAD PDF FROM URL START (RECEIVING END) ===");
    console.log("URL:", url);
    console.log("Token provided:", token ? 'Yes' : 'No');

    // Add null/undefined check
    if (!url || url === 'null' || url === 'undefined' || url === null || url === undefined) {
      console.error("=== LOAD PDF FROM URL ERROR - NULL/INVALID URL ===");
      console.error("URL is null, undefined, or invalid. Cannot load PDF.");
      setIsLoading(false);
      toastAlert("error", "PDF URL is not available. Please contact support.");
      return;
    }

    try {
      const isEncryptedView = url.includes('/api/encrypted-view');
      console.log("Is encrypted view URL?", isEncryptedView);

      const fetchOptions = {
        method: 'GET',
      };

      // Add headers for encrypted view URL
      if (isEncryptedView) {
        fetchOptions.headers = {
          'Accept': 'application/pdf, */*',
        };

        // Use token parameter if provided, otherwise try state
        let tokenToUse = token || authTokenState;
        if (!tokenToUse) {
          try {
            const userData = localStorage.getItem('user_data');
            if (userData) {
              const parsed = JSON.parse(userData);
              tokenToUse = parsed?.token;
              console.log("Token retrieved from localStorage:", tokenToUse ? 'Yes' : 'No');
            }
          } catch (e) {
            console.log('No auth token found in localStorage');
          }
        }

        if (tokenToUse) {
          fetchOptions.headers['Authorization'] = `Bearer ${tokenToUse}`;
          console.log('Using auth token for encrypted view');
        } else {
          console.warn('No auth token available for encrypted view URL');
        }

        console.log('Fetch options for encrypted view:', {
          url,
          method: fetchOptions.method,
          hasAuth: !!tokenToUse,
          headers: Object.keys(fetchOptions.headers || {})
        });
      }

      console.log('Fetching URL:', url);
      const response = await fetch(url, fetchOptions);
      console.log('Response status:', response.status, response.statusText);
      console.log('Response ok:', response.ok);

      // Get content type first
      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);

      // If we get HTML, it's likely an error page
      if (contentType && contentType.includes('text/html')) {
        const errorText = await response.text();
        console.error('Got HTML response instead of PDF. Status:', response.status);
        console.error('HTML content (first 1000 chars):', errorText.substring(0, 1000));
        throw new Error(`Received HTML instead of PDF. Status: ${response.status}`);
      }

      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', response.status, errorText);
        throw new Error(`Failed to load PDF: ${response.status} ${response.statusText}`);
      }

      if (!contentType || !contentType.includes('application/pdf')) {
        const clonedResponse = response.clone();
        const text = await clonedResponse.text();
        console.error('Expected PDF but got:', contentType, text.substring(0, 200));
        throw new Error('Response is not a PDF. Content-Type: ' + contentType);
      }

      const pdfBlob = await response.blob();
      console.log("PDF Blob received:", {
        size: pdfBlob.size,
        type: pdfBlob.type
      });
      console.log("loading ");
      setPdfFile(pdfBlob);
      await convertPdfToImages(pdfBlob);
      console.log("=== LOAD PDF FROM URL SUCCESS (RECEIVING END) ===");
    } catch (error) {
      console.error("=== LOAD PDF FROM URL ERROR (RECEIVING END) ===");
      console.error("Error loading PDF from URL:", error);
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
      setIsLoading(false);
      toastAlert("error", `Failed to load PDF: ${error.message}`);
      throw error;
    }
  };

  // Update the convertPdfToImages function
  const convertPdfToImages = useCallback(async (file) => {
    try {
      let pdfData;
      if (file instanceof Blob) {
        // If file is already a Blob (from file upload)
        pdfData = await file.arrayBuffer();
      } else {
        // If file is a URL
        const response = await fetch(file);
        pdfData = await response.arrayBuffer();
      }

      const loadingTask = pdfjs.getDocument({ data: pdfData });
      const pdf = await loadingTask.promise;
      const images = await Promise.all(
        Array.from({ length: pdf.numPages }, async (_, i) => {
          const page = await pdf.getPage(i + 1);
          const viewport = page.getViewport({ scale: 2 });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({
            canvasContext: context,
            viewport,
          }).promise;

          return canvas.toDataURL("image/png");
        })
      );

      setPdfImages(images);
      setIsLoading(false);
    } catch (error) {
      console.error("Error converting PDF:", error);
    }
  }, []);
  // hashed
  const [documentId, setDocumentId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [encryptedFileIdState, setEncryptedFileIdState] = useState(null);
  const [authTokenState, setAuthTokenState] = useState(null);
  // Function to generate hashed URL
  const generateHashedUrl = (documentId, userId) => {
    const data = { documentId, userId };
    const hashedData = hashIds(data);
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?data=${hashedData}`;
  };

  const [user_email, setUserEmail] = useState("");

  const [signer_currentObject, setSigner_currentObject] = useState(null);

  const [user_idSender, setUser_idSender] = useState(null);
  const [userEmailSender, setUserEmailSender] = useState(null);
  const [senderCompanyName, setSenderCompanyName] = useState("");
  const getUnhashedEmailFileId = async () => {
    // Multi-region (Phase 2c): pin API/link bases from JWT region claim
    if (token) applyRegionFromSigningToken(token);
    //console.log('emailHashed', emailHashed);
    //console.log('fileId', fileId);
    const location = await getUserLocation();
    const postData = {
      email: emailHashed,
      file_id: file_id,
      sender_user_id: senderId,
      token: token,
      location_country: location?.country,
      ip_address: location?.ip,
      location_date: location?.date,
      region: location?.region,
      timezone: location?.timezone,
    };
    try {
      const apiData = await post("file/received-doc-esign", postData); // Specify the endpoint you want to call
      console.log("Update File Controls ");
      console.log(apiData);
      if (apiData.error === true) {
        if (apiData.screen === "DeletedFile") {
          setErrorModal(true);
        } else {
          toastAlert("error", apiData.message);
        }
      } else {
        if (
          apiData.company_details_exist === null ||
          apiData.company_details_exist === undefined
        ) {
          console.log("COMP LOGI ");
          if (apiData?.user_sender?.logo === null) {
            setCompLogo(logoRemoveBg);
          } else {
            setCompLogo(apiData?.user_sender?.logo);
          }
          setCompPrimaryColor("#23b3e8");
          setSenderCompanyName(apiData?.user_sender?.email || "");
        } else {
          console.log("COMP LOGIn h ");
          let comp_logo = apiData?.company_details_exist?.company_logo;
          if (comp_logo === null) {
            console.log("COMP LOGIn hlogog ");

            setCompLogo(logoRemoveBg);
          } else {
            console.log("COMP LOGIn hlogog dsuiyfus");

            setCompLogo(comp_logo);
          }
          let comp_primary_color =
            apiData?.company_details_exist?.primary_color;
          if (comp_primary_color === null) {
            setCompPrimaryColor("#23b3e8");
          } else {
            setCompPrimaryColor(comp_primary_color);
          }
          setSenderCompanyName(
            apiData?.company_details_exist?.company_name || ""
          );
        }
        setSigner_currentObject(apiData?.data);

        setUser_idSender(apiData?.user_sender?.user_id);
        setUserEmailSender(apiData?.user_sender?.email);
        setSelectedSigner(apiData?.data);
        setUniqIdDoc(apiData?.fileDetails?.uniq_id);
        //console.log('You can sign');

        // Helper function to validate UUID format
        const isValidUUID = (str) => {
          if (!str || typeof str !== 'string') return false;
          // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          return uuidRegex.test(str);
        };

        // Decode URL-encoded encryptedFileId from URL params (backend now URL-encodes it)
        let decodedEncryptedFileId = null;
        if (encryptedFileId && encryptedFileId !== 'undefined' && encryptedFileId !== 'null') {
          try {
            decodedEncryptedFileId = decodeURIComponent(encryptedFileId);
            console.log("=== ESIGN RECEIVING END - DECODED ENCRYPTED FILE ID FROM URL ===");
            console.log("Raw encryptedFileId from URL:", encryptedFileId);
            console.log("Decoded encryptedFileId:", decodedEncryptedFileId);
          } catch (error) {
            console.warn("=== ESIGN RECEIVING END - ERROR DECODING ENCRYPTED FILE ID ===");
            console.warn("Error decoding encryptedFileId:", error);
            console.warn("Raw encryptedFileId:", encryptedFileId);
          }
        }

        // Prefer API response value (most reliable), then decoded URL param, then state
        // Validate that encryptedFileId is a valid UUID before using it
        let validEncryptedFileId = null;

        // First priority: API response (most reliable)
        if (apiData?.encryptedFileId && isValidUUID(apiData.encryptedFileId)) {
          validEncryptedFileId = apiData.encryptedFileId;
          console.log("=== ESIGN RECEIVING END - USING ENCRYPTED FILE ID FROM API RESPONSE ===");
          console.log("Encrypted File ID:", validEncryptedFileId);
        }
        // Second priority: Decoded URL param
        else if (decodedEncryptedFileId && isValidUUID(decodedEncryptedFileId)) {
          validEncryptedFileId = decodedEncryptedFileId;
          console.log("=== ESIGN RECEIVING END - USING DECODED ENCRYPTED FILE ID FROM URL ===");
          console.log("Encrypted File ID:", validEncryptedFileId);
        }
        // Third priority: State (from previous extraction)
        else if (encryptedFileIdState && isValidUUID(encryptedFileIdState)) {
          validEncryptedFileId = encryptedFileIdState;
          console.log("=== ESIGN RECEIVING END - USING ENCRYPTED FILE ID FROM STATE ===");
          console.log("Encrypted File ID:", validEncryptedFileId);
        }
        // Log warning if we have values but they're invalid
        else if (apiData?.encryptedFileId || decodedEncryptedFileId || encryptedFileIdState) {
          console.warn("=== ESIGN RECEIVING END - INVALID ENCRYPTED FILE ID ===");
          console.warn("apiData?.encryptedFileId:", apiData?.encryptedFileId);
          console.warn("decodedEncryptedFileId:", decodedEncryptedFileId);
          console.warn("encryptedFileIdState:", encryptedFileIdState);
          console.warn("None of these are valid UUIDs. Falling back to old method.");
        }

        const effectiveEncryptedFileId = validEncryptedFileId;
        const effectiveUserId = apiData?.user_sender?.user_id || userId;
        const effectiveAuthToken = token || authTokenState || apiData?.authToken;

        console.log("=== ESIGN RECEIVING END - PDF URL DECISION ===");
        console.log("encryptedFileId from URL (raw):", encryptedFileId);
        console.log("encryptedFileId from URL (decoded):", decodedEncryptedFileId);
        console.log("encryptedFileIdState:", encryptedFileIdState);
        console.log("apiData?.encryptedFileId:", apiData?.encryptedFileId);
        console.log("effectiveEncryptedFileId (valid UUID):", effectiveEncryptedFileId);
        console.log("effectiveUserId:", effectiveUserId);
        console.log("effectiveAuthToken:", effectiveAuthToken ? 'present' : 'missing');
        console.log("API bgImages:", apiData?.bgImages);
        console.log("inprocess_doc:", apiData?.bgImages?.[0]?.inprocess_doc);

        // Store valid encryptedFileId in state for future use
        if (effectiveEncryptedFileId) {
          setEncryptedFileIdState(effectiveEncryptedFileId);
        }
        if (effectiveAuthToken) {
          setAuthTokenState(effectiveAuthToken);
        }

        // Use encrypted view URL if available and valid
        if (effectiveEncryptedFileId && isValidUUID(effectiveEncryptedFileId) && effectiveUserId) {
          const encryptedViewUrl = `${link_base_url}/api/encrypted-view/${effectiveEncryptedFileId}?user_id=${effectiveUserId}`;
          console.log("=== ESIGN RECEIVING END - USING ENCRYPTED VIEW ===");
          console.log("Encrypted view URL:", encryptedViewUrl);
          console.log("Encrypted File ID:", effectiveEncryptedFileId);
          console.log("User ID:", effectiveUserId);
          setOriginalPdf(encryptedViewUrl);
          loadPdfFromUrl(encryptedViewUrl, effectiveAuthToken);
        } else {
          // Fallback to old method
          const pdfUrl = apiData?.bgImages?.[0]?.inprocess_doc;
          console.log("=== ESIGN RECEIVING END - USING FALLBACK METHOD ===");
          console.log("PDF URL:", pdfUrl);
          console.log("API bgImages:", apiData?.bgImages);
          console.log("bgImages length:", apiData?.bgImages?.length);

          // Add null check before setting and loading
          if (pdfUrl && pdfUrl !== 'null' && pdfUrl !== 'undefined' && pdfUrl !== null && pdfUrl !== undefined) {
            setOriginalPdf(pdfUrl);
            loadPdfFromUrl(pdfUrl);
          } else {
            console.error("=== ESIGN RECEIVING END - NO PDF URL AVAILABLE ===");
            console.error("Both encryptedFileId and pdfUrl are null/undefined");
            console.error("API Response details:", {
              hasBgImages: !!apiData?.bgImages,
              bgImagesLength: apiData?.bgImages?.length,
              firstBgImage: apiData?.bgImages?.[0],
              inprocess_doc: apiData?.bgImages?.[0]?.inprocess_doc,
              bgImages: apiData?.bgImages
            });
            setIsLoading(false);
            toastAlert("error", "PDF file is not available. The document may not have been processed yet. Please contact support.");
          }
        }
        setImageUrlsCount(apiData?.bgImages[0]?.original_doc_pages);
        setImageUrls2(apiData?.bgImages[0]?.image);

        // scroll to top
        console.log(
          "apiData.positions?.[0]?.position_array",
          apiData.positions?.[0]?.position_array
        );
        if (!apiData.error && apiData.positions?.[0]?.position_array) {
          let positionsData = apiData.positions[0].position_array;
          console.log("pos");

          // Parse if string
          if (typeof positionsData === "string") {
            positionsData = JSON.parse(positionsData);
          }

          // Group by page number
          const groupedAnnotations = positionsData.reduce((acc, annotation) => {
            const pageNum = annotation.page_no || 1;
            if (!acc[pageNum]) {
              acc[pageNum] = [];
            }

            // Ensure all required properties are present
            // For signer_date, set today's date as default if no text is provided
            let defaultText = annotation.text || "";
            if (annotation.type === "signer_date" && (!annotation.text || annotation.text === "Date" || annotation.text === "Date..." || annotation.text === "Select a date")) {
              defaultText = format(new Date(), annotation.dateFormat || "MMM dd, yyyy");
            }

            const formattedAnnotation = {
              id: annotation.id,
              type: annotation.type,
              x: annotation.x,
              y: annotation.y,
              width: annotation.width || 150,
              height: annotation.height || 30,
              text: defaultText,
              checked: annotation.checked || false,
              imageUrl: annotation.imageUrl || null,
              format: annotation.format || [],
              fontSize: annotation.fontSize || 14,
              fontFamily: annotation.fontFamily || "Helvetica",
              backgroundColor: annotation.backgroundColor || null,
              borderColor: annotation.borderColor || null,
              signerId: annotation.signerId || null,
              signerColor: annotation.signerColor || null,
              options: annotation.options || [],
              isRequired: annotation.isRequired || false,
              page_no: pageNum,
            };

            acc[pageNum].push(formattedAnnotation);
            return acc;
          }, {});

          setAnnotations(groupedAnnotations);
        }
        if (apiData?.accessCode === true || apiData?.accessCode === "true") {
          console.log("Access code true ");

          setAccessCodeModal(true);
          setAccessCodeSigner(apiData?.data?.access_code);
        } else {
          console.log("Access code falsr ");
        }

        setSignerLoginId(apiData?.data?.signer_id);
        let dt = apiData?.data?.signer_id;
        console.log("SIHNER LOGIN ID ", dt);

        // setAnnotations(apiData?.positions[0]?.position_array);
        // send
        // setAnnotat;

        // end
        let positionsArrady = apiData?.positions[0]?.position_array;

        const email_signer_current = apiData?.data.email;
        setCurrentSignerEmail(email_signer_current);
        console.log("email signef curreny");
        console.log(email_signer_current);
        let all_signers = apiData?.all_signers;
        setAll_RecipientsData(apiData?.all_recipients);
        setSigners(all_signers);

        const currentUser = all_signers.find(
          (signer) => signer.email === email_signer_current
        );
        console.log("currentUser", currentUser);

        const currentUserOrderId = currentUser ? currentUser.order_id : null;
        const statusSignerCompletedDoc = currentUser.completed_status;
        const eSignOrder = apiData.fileDetails.set_esigning_order;
        setUniqIdDoc(apiData?.fileDetails?.uniq_id);
        setStatusFile(apiData?.fileDetails?.status);
        setFileName(apiData?.fileDetails?.name);
        setFileUserid(apiData?.fileDetails?.user_id);
        setDocSignerStatus(currentUser.completed_status);

        if (
          currentUser.completed_status === true ||
          currentUser.completed_status === "true"
        ) {
          console.log("setDocSignerStatusModal");

          setDocSignerStatusModal(true);
          //console.log('DD');
        } else {
          console.log(currentUserOrderId);
          console.log("currentUserOrderId");
          // if (eSignOrder === true || eSignOrder === "true") {
          if (currentUserOrderId === null) {
            console.log("Current user not found in signers");
          } else {
            console.log("Current user  found in signers");

            // Check if all users with a lower order_id have status true
            // Sort the orders by order_id
            all_signers.sort(
              (a, b) => parseInt(a.order_id) - parseInt(b.order_id)
            );
            // Find the first order with completed_status null
            let lowestOrderWithNullStatus = all_signers.find(
              (order) => order.completed_status === null
            );

            if (lowestOrderWithNullStatus) {
              console.log("Current user  null aagy ");

              console.log(lowestOrderWithNullStatus.order_id);
              console.log(currentUserOrderId);

              //console.log('Lowest order id with null completed status:', lowestOrderWithNullStatus.order_id);
              if (lowestOrderWithNullStatus.order_id === currentUserOrderId) {
                console.log("Current user  null aagy value  ");

                setWaitforOtherusers(false);
                setActiveSignerId(currentUser.signer_id);

                let signersWhoHaveCompletedSignedAA = all_signers.filter(
                  (order) => order.completed_status === "true"
                );
                //console.log('signers Who Have Completed Signed');
                //console.log(signersWhoHaveCompletedSignedAA);
                if (!Array.isArray(signersWhoHaveCompletedSignedAA)) {
                  signersWhoHaveCompletedSignedAA = [
                    signersWhoHaveCompletedSignedAA,
                  ];
                }

                const signerIds = signersWhoHaveCompletedSignedAA.map(
                  (signer) => signer.signer_id
                );
                //console.log('Signer IDs:');
                //console.log(signerIds);

                setSignersWhoHaveCompletedSigning(signerIds);
              } else {
                console.log("Current user sdfhj  ");

                //console.log('Wait for other users to sign');
                setWaitforOtherusers(true);
                setWaitOtherModal(true);
              }
            } else {
              //console.log('No order with null completed status found.');
            }
          }
          // } else {
          //   setWaitforOtherusers(false);
          //   setActiveSignerId(currentUser.signer_id);
          // }
          //console.log('dsf');
        }
      }
    } catch (error) {
      //console.log('Error fetching data:', error);
    }
  };
  // Helper function to validate UUID format
  const isValidUUID = (str) => {
    if (!str || typeof str !== 'string') return false;
    // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  // Modify your useEffect to include the loader
  useEffect(() => {
    console.log("=== ESIGN RECEIVING END - URL PARAMS DEBUG ===");
    console.log("emailHashed:", emailHashed);
    console.log("file_id:", file_id);
    console.log("senderId:", senderId);
    console.log("token:", token ? 'present' : 'missing');
    console.log("encryptedFileId:", encryptedFileId ? 'present' : 'missing');
    if (encryptedFileId) {
      console.log("encryptedFileId value:", encryptedFileId);
      console.log("encryptedFileId is valid UUID:", isValidUUID(encryptedFileId));
    }

    setIsLoading(true);

    // Store encryptedFileId from URL params if available and valid
    // Backend now URL-encodes the encryptedFileId, so we need to decode it
    if (encryptedFileId && encryptedFileId !== 'undefined' && encryptedFileId !== 'null') {
      try {
        const decodedEncryptedFileId = decodeURIComponent(encryptedFileId);
        console.log("=== ESIGN RECEIVING END - ENCRYPTED FILE ID FROM URL ===");
        console.log("Raw encryptedFileId from URL:", encryptedFileId);
        console.log("Decoded encryptedFileId:", decodedEncryptedFileId);

        // Validate it's a valid UUID before storing
        if (isValidUUID(decodedEncryptedFileId)) {
          console.log("Encrypted File ID (valid UUID):", decodedEncryptedFileId);
          setEncryptedFileIdState(decodedEncryptedFileId);
        } else {
          console.warn("=== ESIGN RECEIVING END - INVALID ENCRYPTED FILE ID IN URL ===");
          console.warn("Decoded encryptedFileId is not a valid UUID:", decodedEncryptedFileId);
          console.warn("Will rely on API response instead.");
        }
      } catch (error) {
        console.warn("=== ESIGN RECEIVING END - ERROR DECODING ENCRYPTED FILE ID ===");
        console.warn("Error decoding encryptedFileId:", error);
        console.warn("Raw encryptedFileId:", encryptedFileId);
        console.warn("Will rely on API response instead.");
      }
    } else {
      console.log("=== ESIGN RECEIVING END - NO ENCRYPTED FILE ID IN URL ===");
      console.log("encryptedFileId param is:", encryptedFileId);
      console.log("Will check API response for encryptedFileId.");
    }

    // Store token from URL params if available
    if (token && token !== 'undefined' && token !== 'null') {
      console.log("=== ESIGN RECEIVING END - TOKEN FROM URL ===");
      console.log("Token present:", token ? 'Yes' : 'No');
      setAuthTokenState(token);
    }

    getUnhashedEmailFileId();
    // setIsLoading(false);
  }, []);
  const [originalPdf, setOriginalPdf] = useState("");
  const [imageUrlsCount, setImageUrlsCount] = useState("");

  const [imageUrls2, setImageUrls2] = useState("");

  // Track originalPdf changes
  useEffect(() => {
    if (originalPdf) {
      console.log("=== ORIGINAL PDF URL CHANGED (RECEIVING END) ===");
      console.log("New originalPdf URL:", originalPdf);
      console.log("Is encrypted URL?", originalPdf?.includes('encrypted-view'));
      console.log("URL length:", originalPdf?.length || 0);
    }
  }, [originalPdf]);
  const [accessCodeModal, setAccessCodeModal] = useState(false);
  const [accessCodeSigner, setAccessCodeSigner] = useState("");
  const [signerLoginId, setSignerLoginId] = useState("");
  const [currentSignerEmail, setCurrentSignerEmail] = useState("");

  // Pre-fill KBA email when available
  useEffect(() => {
    if (isKbaRequired && currentSignerEmail) {
      setKbaEmail(currentSignerEmail);
    }
  }, [isKbaRequired, currentSignerEmail]);

  // Log when KBA modal opens and check URL
  useEffect(() => {
    if (kbaModalOpen) {
      console.log("=== KBA MODAL OPENED ===");
      console.log("kbaVerifyUrl:", kbaVerifyUrl);
      console.log("kbaSessionId:", kbaSessionId);
      console.log("URL is valid:", kbaVerifyUrl && kbaVerifyUrl.trim() !== "");

      // Check if iframe loaded after a delay
      // If external site blocks iframe, it might redirect parent window
      const checkIframeLoad = setTimeout(() => {
        if (kbaModalOpen && !iframeLoadAttempted) {
          console.warn("Iframe may not have loaded - external site might block iframe embedding");
          // If iframe hasn't loaded after 2 seconds, it might be blocked
          // We'll keep the modal open and let user see the iframe (even if blank)
          // The external site might redirect the parent window, which we can't prevent
        }
      }, 2000);

      return () => clearTimeout(checkIframeLoad);
    } else {
      // Reset iframe load attempt when modal closes
      setIframeLoadAttempted(false);
    }
  }, [kbaModalOpen, kbaVerifyUrl, kbaSessionId, iframeLoadAttempted]);

  // Prevent parent window from being redirected by iframe
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // If we're in KBA modal, don't allow navigation away
      if (kbaModalOpen) {
        // Allow navigation but log it
        console.log("Navigation detected while KBA modal is open");
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [kbaModalOpen]);

  // Function to check KBA status
  const checkKbaStatus = useCallback(async (sessionIdParam = null) => {
    const sessionId = sessionIdParam || kbaSessionId || localStorage.getItem("kba_session_id");

    if (!sessionId) {
      console.warn("No sessionId available to check KBA status");
      return null;
    }

    try {
      const apiData = await get(`api/kba/status/${sessionId}`);

      if (apiData.error) {
        console.error("Failed to check KBA status:", apiData.message);
        return null;
      }

      return apiData.data;
    } catch (error) {
      console.error("Error checking KBA status:", error);
      return null;
    }
  }, [kbaSessionId]);

  // Listen for messages from KBA iframe (when verification completes)
  useEffect(() => {
    const handleMessage = async (event) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return;
      }

      // Check if message is from KBA verification
      if (event.data && event.data.type === "KBA_COMPLETE" && kbaSessionId) {
        console.log("KBA verification completed, checking status...");

        // Check KBA status
        const statusData = await checkKbaStatus();

        if (statusData && statusData.canProceed) {
          toastAlert("success", "KBA verification completed successfully!");
          setKbaModalOpen(false);
          setKbaVerified(true);
          setKbaVerificationStatus("passed");
          // User can now proceed to sign the document
        } else {
          toastAlert("error", "KBA verification failed. Please try again.");
          setKbaVerificationStatus("failed");
        }
      }
    };

    if (kbaModalOpen) {
      window.addEventListener("message", handleMessage);
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, [kbaModalOpen, kbaSessionId, checkKbaStatus]);

  // Check KBA status on component mount and when URL params change
  useEffect(() => {
    if (!isKbaRequired) {
      return;
    }

    const checkKbaStatusFromParams = async () => {
      // Check URL params for kba_success or kba_failed
      const urlParams = new URLSearchParams(location.search);
      const kbaSuccess = urlParams.get("kba_success");
      const kbaFailed = urlParams.get("kba_failed");
      const sessionIdFromUrl = urlParams.get("sessionId");

      // Get sessionId from URL, localStorage, or state
      const sessionId = sessionIdFromUrl || kbaSessionId || localStorage.getItem("kba_session_id");

      if (kbaSuccess === "true" && sessionId) {
        console.log("KBA success detected in URL, verifying status...");
        // Check localStorage for completion status
        const completionStatus = localStorage.getItem(`kba_completion_${sessionId}`);

        if (completionStatus === "passed") {
          setKbaVerified(true);
          setKbaVerificationStatus("passed");
          toastAlert("success", "KBA verification completed successfully!");

          // Clean up URL params
          const newUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, "", newUrl);
        } else {
          // Verify with backend
          const statusData = await checkKbaStatus();
          if (statusData && statusData.canProceed) {
            setKbaVerified(true);
            setKbaVerificationStatus("passed");
            toastAlert("success", "KBA verification completed successfully!");

            // Clean up URL params
            const newUrl = window.location.pathname + window.location.hash;
            window.history.replaceState({}, "", newUrl);
          }
        }
      } else if (kbaFailed === "true") {
        console.log("KBA failed detected in URL");
        setKbaVerificationStatus("failed");
        toastAlert("error", "KBA verification failed. Please try again.");

        // Clean up URL params
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, "", newUrl);
      } else if (sessionId) {
        // Check localStorage for completion status
        const completionStatus = localStorage.getItem(`kba_completion_${sessionId}`);
        if (completionStatus === "passed") {
          setKbaVerified(true);
          setKbaVerificationStatus("passed");
        } else if (completionStatus === "failed") {
          setKbaVerificationStatus("failed");
        }
      }
    };

    checkKbaStatusFromParams();
  }, [isKbaRequired, location.search, kbaSessionId, checkKbaStatus]);

  // Poll for KBA status if required and not yet verified
  useEffect(() => {
    if (!isKbaRequired || kbaVerified || kbaVerificationStatus === "failed") {
      return;
    }

    const sessionId = kbaSessionId || localStorage.getItem("kba_session_id");
    if (!sessionId) {
      return;
    }

    // Start polling every 5 seconds
    const pollInterval = setInterval(async () => {
      console.log("Polling KBA status...");
      const statusData = await checkKbaStatus();

      if (statusData) {
        if (statusData.status === "passed" && statusData.canProceed) {
          setKbaVerified(true);
          setKbaVerificationStatus("passed");
          toastAlert("success", "KBA verification completed successfully!");
          clearInterval(pollInterval);
        } else if (statusData.status === "failed") {
          setKbaVerificationStatus("failed");
          toastAlert("error", "KBA verification failed. Please try again.");
          clearInterval(pollInterval);
        }
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [isKbaRequired, kbaVerified, kbaVerificationStatus, kbaSessionId, checkKbaStatus]);

  // Window focus detection - check status immediately when user returns to tab
  useEffect(() => {
    if (!isKbaRequired || kbaVerified || kbaVerificationStatus === "failed") {
      return;
    }

    const handleFocus = async () => {
      console.log("Window focused, checking KBA status...");
      const sessionId = kbaSessionId || localStorage.getItem("kba_session_id");
      if (sessionId) {
        const statusData = await checkKbaStatus();
        if (statusData) {
          if (statusData.status === "passed" && statusData.canProceed) {
            setKbaVerified(true);
            setKbaVerificationStatus("passed");
            toastAlert("success", "KBA verification completed successfully!");
          } else if (statusData.status === "failed") {
            setKbaVerificationStatus("failed");
            toastAlert("error", "KBA verification failed. Please try again.");
          }
        }
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [isKbaRequired, kbaVerified, kbaVerificationStatus, kbaSessionId, checkKbaStatus]);

  const [activeAnnotation, setActiveAnnotation] = useState(null);

  // Update the initial image dimensions calculation - matching EsignSetup.js
  const getInitialImageDimensions = (event, pageElement, pageNum) => {
    const imageElem = pageElement.querySelector(".pdf-image");
    if (!imageElem) return { xPercent: 0, yPercent: 0 };

    // Get the actual rendered PDF dimensions (not container dimensions)
    const imageRect = imageElem.getBoundingClientRect();

    // Calculate click position relative to the PDF image itself
    const relativeX = event.clientX - imageRect.left;
    const relativeY = event.clientY - imageRect.top;

    // Convert to percentage of the actual PDF dimensions
    const xPercent = (relativeX / imageRect.width) * 100;
    const yPercent = (relativeY / imageRect.height) * 100;

    // Ensure coordinates are within bounds
    return {
      xPercent: Math.max(0, Math.min(100, xPercent)),
      yPercent: Math.max(0, Math.min(100, yPercent)),
    };
  };
  // Add this function to get default dimensions for different types
  const getDefaultDimensions = (type, scale) => {
    switch (type) {
      case "signer_initials":
        return {
          width: 250 / scale,
          height: 100 / scale,
        };
      case "my_signature":
        return {
          width: 250 / scale,
          height: 100 / scale,
        };
      case "signer_initials_text":
        return {
          width: 100 / scale,
          height: 100 / scale,
        };
      case "my_initials":
        return {
          width: 100 / scale,
          height: 100 / scale,
        };
      case "signer_chooseImgStamp":
        return {
          width: 150 / scale,
          height: 150 / scale, // Square for stamps
        };
      case "signer_chooseImgDrivingL":
        return {
          width: 350 / scale,
          height: 160 / scale, // Aspect ratio close to typical license
        };
      case "signer_chooseImgPassportPhoto":
        return {
          width: 250 / scale,
          height: 250 / scale, // Standard passport photo ratio
        };
      default:
        return {
          width: 150 / scale,
          height: 60 / scale,
        };
    }
  };
  useEffect(() => {
    if (pdfImages.length > 0) {
      const img = new Image();
      img.onload = () => {
        setFirstPageHeight(img.height);
      };
      img.src = pdfImages[0];
    }
  }, [pdfImages]);
  // Add this useEffect to automatically select the last added signer
  useEffect(() => {
    if (signers.length > 0) {
      setSelectedSignerId(signers[signers.length - 1]);
      setSelectedSigner(signers[signers.length - 1]);
      setActive("2");
    }
  }, [signers]);
  const [isInteracting, setIsInteracting] = useState(false);
  const handleSignatureComplete = async (signatureData, typeData) => {
    // Close modal immediately when signature is completed
    setSignatureModalOpen(false);

    if (!editingAnnotation) return;

    const { pageNum, index } = editingAnnotation;
    const annotationKey = `${pageNum}-${index}`;

    // Mark annotation as loading
    setLoadingAnnotations((prev) => new Set(prev).add(annotationKey));

    try {
      // Crop white space from signature/initial image
      const croppedSignatureData = await cropWhiteSpace(signatureData);

      // Immediately update with cropped base64 data for quick display
      setAnnotations((prev) => {
        const updatedAnnotations = { ...prev };
        updatedAnnotations[pageNum][index] = {
          ...updatedAnnotations[pageNum][index],
          imageUrl: croppedSignatureData, // Use cropped base64 immediately for quick display
        };
        return updatedAnnotations;
      });

      // Convert cropped base64 to file
      const base64Response = await fetch(croppedSignatureData);
      const blob = await base64Response.blob();
      const fileObject = new File([blob], "signature.png", { type: "image/png" });

      // Upload to API
      const postData = {
        image: fileObject,
        user_id: user_idSender,
      };

      const apiData = await postFormData(postData);
      console.log("apiData", apiData);

      if (!apiData.public_url) {
        toastAlert("error", "Error uploading Files");
        setLoadingAnnotations((prev) => {
          const newSet = new Set(prev);
          newSet.delete(annotationKey);
          return newSet;
        });
        return;
      }

      const ImageUrl = apiData.public_url;

      // Update with final URL
      setAnnotations((prev) => {
        const updatedAnnotations = { ...prev };
        updatedAnnotations[pageNum][index] = {
          ...updatedAnnotations[pageNum][index],
          imageUrl: ImageUrl,
        };
        return updatedAnnotations;
      });
    } catch (error) {
      console.error("Error uploading signature:", error);
      toastAlert("error", "Error uploading Files");
    } finally {
      // Remove loading state
      setLoadingAnnotations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(annotationKey);
        return newSet;
      });
      // Clean up state
      setEditingAnnotation(null);
      setInitialBox(false);
    }
  };

  const shouldLockAspectRatio = (type) => {
    const typesWithLockedAspectRatio = [
      "stamp",
      "highlight",
      "my_signature",
      "my_initials",
      "signer_initials",
      "signer_initials_text",
      "signer_chooseImgStamp",
      "signer_chooseImgDrivingL",
      "signer_chooseImgPassportPhoto",
    ];
    return typesWithLockedAspectRatio.includes(type);
  };
  // 1. Add a new state for tracking the annotation being edited
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  // Track loading state for annotations
  const [loadingAnnotations, setLoadingAnnotations] = useState(new Set());

  // Function to crop white space from signature/initial images
  const cropWhiteSpace = (base64Image) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Draw image to canvas
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Find bounding box of non-white pixels
        let minX = canvas.width;
        let minY = canvas.height;
        let maxX = 0;
        let maxY = 0;

        // Threshold for "white" (adjust if needed, 250 means very light colors are considered white)
        const whiteThreshold = 250;

        for (let y = 0; y < canvas.height; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const idx = (y * canvas.width + x) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            const a = data[idx + 3];

            // Check if pixel is not white/transparent
            if (a > 0 && (r < whiteThreshold || g < whiteThreshold || b < whiteThreshold)) {
              minX = Math.min(minX, x);
              minY = Math.min(minY, y);
              maxX = Math.max(maxX, x);
              maxY = Math.max(maxY, y);
            }
          }
        }

        // Keep typed signatures left-flush while preserving breathing room
        // on top/right/bottom to avoid clipping descenders.
        const paddingLeft = 0;
        const paddingRight = 5;
        const paddingTop = 5;
        const paddingBottom = 5;
        minX = Math.max(0, minX - paddingLeft);
        minY = Math.max(0, minY - paddingTop);
        maxX = Math.min(canvas.width, maxX + paddingRight);
        maxY = Math.min(canvas.height, maxY + paddingBottom);

        // If no content found, return original
        if (minX >= maxX || minY >= maxY) {
          resolve(base64Image);
          return;
        }

        // Create new canvas with cropped dimensions
        const croppedCanvas = document.createElement("canvas");
        const croppedCtx = croppedCanvas.getContext("2d");
        const width = maxX - minX;
        const height = maxY - minY;

        croppedCanvas.width = width;
        croppedCanvas.height = height;

        // Draw cropped portion
        croppedCtx.drawImage(
          img,
          minX, minY, width, height,
          0, 0, width, height
        );

        // Convert to base64
        const croppedBase64 = croppedCanvas.toDataURL("image/png");
        resolve(croppedBase64);
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = base64Image;
    });
  };

  // 2. Add handler for editing signature
  const handleEditSignature = (pageNum, index, annot) => {
    // Reset state before opening
    setEditingAnnotation(null);
    setInitialBox(false);
    // Small delay to ensure clean state
    setTimeout(() => {
      const shouldBeInitialBox = annot.type === "signer_initials_text" || annot.type === "my_initials";
      setEditingAnnotation({ pageNum, index, annot });
      setInitialBox(shouldBeInitialBox);
      setSignatureModalOpen(true);
    }, 0);
  };

  // 3. Add handler for editing stamp
  const handleEditStamp = (pageNum, index, annot) => {
    pendingStampActionRef.current = { isEdit: true, editData: { pageNum, index, annot } };
    setEditingAnnotation({ pageNum, index, annot });
    // #region agent log
    try {
      fetch('http://127.0.0.1:7246/ingest/8b376689-945a-4a97-94d5-d387ea1b8602', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: 'debug-session', runId: 'placement-pre', hypothesisId: 'P3', location: 'src/pages/EsignReceivingEndCopy.js:handleEditStamp', message: 'edit stamp start', data: { pageNum, index, type: annot?.type, annotXY: { x: annot?.x, y: annot?.y }, activePage, pageNoClicked, isMobile: !!isMobile, pdfZoomScale }, timestamp: Date.now() }) }).catch(() => { });
    } catch (_) { }
    // #endregion
    // Reset input value first to ensure iOS triggers onChange (important for iOS)
    if (stampInputRef.current) {
      stampInputRef.current.value = "";
    }
    // Use setTimeout to ensure iOS processes the click properly
    setTimeout(() => {
      if (stampInputRef.current) {
        stampInputRef.current.click();
      }
    }, 10);
  };
  const [lastClickPosition, setLastClickPosition] = useState(null);

  // Inside App.js, modify the handlePdfClick function
  const [loadingImageIndex, setLoadingImageIndex] = useState(null);

  const handleAnnotationClick = (pageNum, index, annotation) => {
    console.log("clicked texct ");
    console.log("clicked texct ", annotation);

    const annotationId = `${pageNum}-${index}`;
    setActiveAnnotationId(annotationId);
    setActiveAnnotation(annotationId);
    setSelectedAnnotation({
      ...annotation,
      pageNum,
      index,
    });
    // Set focused annotation
    setFocusedAnnotation(annotationId);
  };
  const handleImageUpload = (pageNum, index, annot, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoadingImageIndex(`${pageNum}-${index}`); // Set loader for this annotation

    const reader = new FileReader();
    reader.onload = (e) => {
      setTimeout(() => {
        setCropSrc(e.target.result);
        setPendingStampAction({
          isEdit: true,
          editData: { pageNum, index, annot },
        });
        setCropperModalOpen(true);
        setLoadingImageIndex(null); // Remove loader once loaded
      }, 1500); // Simulate loading delay
    };
    reader.readAsDataURL(file);
  };
  const handleAnnotationTextChange = (e, pageNum, index) => {
    const newText = e.target.value;
    const inputEl = annotationRefs.current[`${pageNum}-${index}`];

    if (!inputEl) return;

    // Create temporary span to measure text width
    const span = document.createElement("span");
    const computedStyle = window.getComputedStyle(inputEl);

    span.style.cssText = `
      visibility: hidden;
      position: fixed;
      font-family: ${computedStyle.fontFamily};
      font-size: ${computedStyle.fontSize};
      font-weight: ${computedStyle.fontWeight};
      font-style: ${computedStyle.fontStyle};
      letter-spacing: ${computedStyle.letterSpacing};
      white-space: pre;
      padding: ${computedStyle.padding};
    `;
    span.innerText = newText;
    document.body.appendChild(span);

    // Get available width (container width - padding)
    const padding = 8; // Total horizontal padding
    const containerWidth = inputEl.offsetWidth - padding;
    const textWidth = span.getBoundingClientRect().width;

    document.body.removeChild(span);

    // Only update text if it fits within the container
    if (textWidth <= containerWidth) {
      setAnnotations((prev) => {
        const pageAnns = [...(prev[pageNum] || [])];
        pageAnns[index] = {
          ...pageAnns[index],
          text: newText,
        };
        return { ...prev, [pageNum]: pageAnns };
      });
    }
  };
  const [isDeleting, setIsDeleting] = useState(false);

  // Memoize render image function
  const renderImage = useCallback((image, index, isMainView = true) => {
    return (
      <img
        src={image}
        alt={`Page ${index + 1}`}
        className={isMainView ? "pdf-image" : "thumbnail-image"}
        loading="lazy"
        style={isMainView ? {
          width: "100%",
          height: "auto",
          display: "block",
          objectFit: "contain",
          maxHeight: "none",
        } : undefined}
      />
    );
  }, []);
  // text
  // useEffect(() => {
  //   console.log("annoat", annotations);
  // }, [annotations]);

  // Helper function to calculate text width based on font size and font family (same as EsignSetupCopy.js)
  const calculateTextWidth = useCallback((
    text,
    fontSize = 16,
    fontFamily = "Helvetica"
  ) => {
    if (!text || !text.trim()) {
      return 0; // Return 0 for empty text
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Use PDF-equivalent font size for measurement to match PDF behavior
    // PDF uses 72 DPI (points), CSS uses 96 DPI (pixels)
    // This ensures text width calculation matches what PDF will render
    const pdfFontSize = fontSize * (72 / 96);
    ctx.font = `${pdfFontSize}px ${fontFamily}`;

    // Measure text at PDF-equivalent font size
    const textWidthAtPdfSize = ctx.measureText(text).width;

    // Scale back up to CSS pixel space for comparison with screen dimensions
    const textWidth = Math.ceil(textWidthAtPdfSize * (96 / 72));

    return textWidth;
  }, []);

  // Helper function to calculate text height based on wrapped text (same as EsignSetupCopy.js)
  const calculateTextHeight = useCallback((
    text,
    fontSize = 16,
    fontFamily = "Helvetica, Arial, sans-serif",
    availableWidth,
    lineHeight = 1
  ) => {
    if (!text || !text.trim()) {
      // Use exact fontSize * lineHeight without any minimum padding
      return fontSize * lineHeight;
    }

    // Use a temporary DOM element for more accurate measurement
    const tempDiv = document.createElement("div");
    tempDiv.style.cssText = `
      position: absolute;
      visibility: hidden;
      width: ${availableWidth}px;
      font-size: ${fontSize}px;
      font-family: ${fontFamily};
      line-height: ${lineHeight};
      white-space: pre-wrap;
      word-wrap: break-word;
      word-break: break-word;
      overflow-wrap: break-word;
      padding: 0;
      margin: 0;
      box-sizing: border-box;
    `;
    tempDiv.textContent = text;
    document.body.appendChild(tempDiv);

    // Get the actual rendered height
    const actualHeight = tempDiv.offsetHeight;
    document.body.removeChild(tempDiv);

    // Use exact height - no minimum padding to avoid extra space
    const minHeight = fontSize * lineHeight; // Minimum height for one line based on actual font size

    return Math.max(minHeight, actualHeight);
  }, []);

  // Helper function to calculate date field width based on actual date content
  const calculateDateWidth = useCallback((dateText, fontSize, fontFamily, pageWidth, isMobile = false) => {
    if (!dateText || dateText === "Date" || dateText === "Date..." || dateText === "Select a date") {
      // Use placeholder text for width calculation
      dateText = "MMM dd, yyyy"; // Full date format placeholder
    }

    // Create temporary span to measure text width
    const span = document.createElement("span");
    span.style.cssText = `
      visibility: hidden;
      position: fixed;
      font-family: ${fontFamily || "Helvetica, Arial, sans-serif"};
      font-size: ${fontSize || 15}px;
      font-weight: normal;
      font-style: normal;
      letter-spacing: 0;
      white-space: pre;
      padding: 3px 6px;
    `;
    span.innerText = dateText;
    document.body.appendChild(span);

    const textWidthPx = Math.ceil(span.getBoundingClientRect().width);
    document.body.removeChild(span);

    // Add buffer for padding and border - smaller buffer on mobile
    const bufferPx = isMobile ? 8 : 12; // Reduced buffer on mobile to prevent excessive width
    const totalWidthPx = textWidthPx + bufferPx;

    // Convert to percentage of page width
    const widthPercent = (totalWidthPx / pageWidth) * 100;

    return widthPercent;
  }, []);

  const renderAnnotations = useCallback(
    (pageNum) => {
      const pageAnnotations = annotations[pageNum] || [];
      if (!Array.isArray(pageAnnotations)) {
        console.warn(
          `Page ${pageNum} annotations is not an array:`,
          pageAnnotations
        );
        return null;
      }
      const imageElem = document.querySelector(`#img-${pageNum} .pdf-image`);
      // if (!imageElem) return null; // Safe check if page not rendered yet
      if (!imageElem) {
        console.warn(`Image element not found for page ${pageNum}`);
        return null;
      }

      // Get the rendered PDF dimensions.
      // IMPORTANT (mobile): PDF pages can be pinch-zoomed (CSS transform via TransformWrapper).
      // If we use getBoundingClientRect() (post-transform) for %→px conversion, overlays drift
      // because the browser scales positioned overlays again ("double scaling").
      // On mobile use the overlay's containing block (the page container div) for dimensions
      // so that overlay left/top in px match the same coordinate system; use rect on web.
      const imageRect = imageElem.getBoundingClientRect();
      const layoutW = imageElem.offsetWidth || imageElem.clientWidth || 0;
      const layoutH = imageElem.offsetHeight || imageElem.clientHeight || 0;
      let pageWidth;
      let pageHeight;
      if (isMobile) {
        // Prefer image layout dimensions (same as overlay coordinate system when container wraps image).
        if (layoutW > 0 && layoutH > 0) {
          pageWidth = layoutW;
          pageHeight = layoutH;
        } else {
          const containerElem = document.getElementById(`img-${pageNum}`);
          const containerW = containerElem ? (containerElem.offsetWidth || containerElem.clientWidth || 0) : 0;
          const containerH = containerElem ? (containerElem.offsetHeight || containerElem.clientHeight || 0) : 0;
          if (containerW > 0 && containerH > 0) {
            pageWidth = containerW;
            pageHeight = containerH;
          } else {
            pageWidth = imageRect?.width || 0;
            pageHeight = imageRect?.height || 0;
          }
        }
      } else {
        pageWidth = imageRect?.width || 0;
        pageHeight = imageRect?.height || 0;
      }
      // DevTools/mobile can briefly report height=0 even when the image is visible.
      // Fallback to offsetHeight or natural aspect ratio to keep annotations stable (mobile only).
      if (isMobile && pageWidth > 0 && pageHeight <= 0) {
        const containerElem = document.getElementById(`img-${pageNum}`);
        const fallbackH = containerElem ? (containerElem.offsetHeight || containerElem.clientHeight || 0) : (layoutH || imageElem.offsetHeight || imageElem.clientHeight || 0);
        if (fallbackH > 0) {
          pageHeight = fallbackH;
        } else if (imageElem.naturalWidth > 0 && imageElem.naturalHeight > 0) {
          pageHeight = pageWidth * (imageElem.naturalHeight / imageElem.naturalWidth);
        }
      }

      // If still invalid, fall back to last-known good size from `pageSizes`.
      if (pageWidth > 0 && pageHeight <= 0) {
        const cached = pageSizes?.[pageNum];
        if (cached?.width > 0 && cached?.height > 0) {
          pageWidth = cached.width;
          pageHeight = cached.height;
        }
      }

      if (!imageRect || pageWidth <= 0 || pageHeight <= 0) {
        console.warn(`Invalid image dimensions for page ${pageNum}`);
        return null;
      }
      let didLogMobilePlacement = false;
      return pageAnnotations.map((annot, index) => {
        if (!annot || typeof annot !== "object") {
          console.warn(
            `Invalid annotation at page ${pageNum}, index ${index}:`,
            annot
          );
          return null;
        }
        // ✅ Always calculate pixel positions from percentage-based box positions (same as EsignSetupCopy.js)
        // Calculate pixel positions from percentages (always use box positions)
        const pixelX = (annot.x / 100) * pageWidth;
        const pixelY = (annot.y / 100) * pageHeight;
        let pixelWidth = (annot.width / 100) * pageWidth;
        const pixelHeight = (annot.height / 100) * pageHeight;

        // #region agent log
        if (
          !didLogMobilePlacement &&
          isMobile &&
          ["signer_initials", "signer_initials_text", "signer_chooseImgStamp", "signer_chooseImgDrivingL", "signer_chooseImgPassportPhoto"].includes(annot.type)
        ) {
          didLogMobilePlacement = true;
          const containerElem = document.getElementById(`img-${pageNum}`);
          const containerRect = containerElem?.getBoundingClientRect?.();
          const dx = containerRect && imageRect ? (imageRect.left - containerRect.left) : null;
          const dy = containerRect && imageRect ? (imageRect.top - containerRect.top) : null;
          const offsetW = imageElem.offsetWidth || 0;
          const offsetH = imageElem.offsetHeight || 0;
          fetch('http://127.0.0.1:7246/ingest/8b376689-945a-4a97-94d5-d387ea1b8602', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: 'debug-session', runId: 'placement-pre', hypothesisId: 'P1', location: 'src/pages/EsignReceivingEndCopy.js:renderAnnotations(placement)', message: 'mobile placement snapshot', data: { pageNum, index, type: annot.type, annot: { x: annot.x, y: annot.y, w: annot.width, h: annot.height }, page: { w: pageWidth, h: pageHeight }, pixel: { x: pixelX, y: pixelY, w: pixelWidth, h: pixelHeight }, activePage, pdfZoomScale, imgRect: imageRect ? { left: imageRect.left, top: imageRect.top, width: imageRect.width, height: imageRect.height } : null, containerRect: containerRect ? { left: containerRect.left, top: containerRect.top, width: containerRect.width, height: containerRect.height } : null, offset: { w: offsetW, h: offsetH }, imgOffsetInContainer: { dx, dy } }, timestamp: Date.now() }) }).catch(() => { });
        }
        // #endregion

        // For date fields, calculate minimum width based on date text content (not backend width)
        if (annot.type === "signer_date") {
          const dateText = annot.text || "MMM dd, yyyy"; // Use actual date text or placeholder
          const fontSize = annot.fontSize || 21;
          const fontFamilyFull = annot.fontFamily || "Helvetica, Arial, sans-serif";
          // Extract first font from font stack for calculateTextWidth
          const fontFamily = fontFamilyFull.split(",")[0].trim();

          // Calculate minimum width needed for date text (match mobile font scaling used for previewFontSizePx)
          const dateFontSizePx = (() => {
            const base = Number(fontSize || 21);
            const clampedBase = Math.max(10, Math.min(72, base));
            if (!isMobile) return clampedBase;
            const baselinePageWidth = 800;
            let factor = pageWidth > 0 ? Math.min(1, pageWidth / baselinePageWidth) : 1;
            factor *= 0.85;
            const scaled = Math.max(9, Math.min(72, clampedBase * factor));
            return Math.min(12, scaled); // Same cap as previewFontSizePx on mobile
          })();
          let calculatedWidthPx = calculateTextWidth(dateText, dateFontSizePx, fontFamily);

          // If text is empty or very short, use a small default
          if (calculatedWidthPx === 0 || calculatedWidthPx < 20) {
            calculatedWidthPx = 20; // Minimum for empty text
          }

          // Add buffer to prevent text cutoff
          const bufferPx = isMobile ? 8 : 20; // Smaller buffer on mobile
          const desiredWidthPx = calculatedWidthPx + bufferPx;

          // Prevent overflow past the page's right edge
          const maxAllowedWidthPx = Math.max(0, pageWidth - pixelX);

          if (isMobile) {
            // Mobile: match web-like behavior (size to content; wrap when touching page edge)
            pixelWidth = Math.min(desiredWidthPx, maxAllowedWidthPx);
          } else {
            // Desktop/web: respect any user-resized width, but never allow too small for the date text
            pixelWidth = Math.max(pixelWidth, desiredWidthPx);
            pixelWidth = Math.min(pixelWidth, maxAllowedWidthPx);
          }
        }

        // Use pixelHeight directly from box position (same as EsignSetupCopy.js)
        // Always use the calculated pixelHeight from box position
        const annotationHeight = pixelHeight;
        const isMobileTextOrDate =
          isMobile && (annot.type === "signer_text" || annot.type === "signer_date");
        const textRaw = typeof annot.text === "string" ? annot.text : "";
        const textTrimmed = textRaw.trim();
        const isTextPlaceholder = annot.type === "signer_text" && !textTrimmed;
        const isDatePlaceholder =
          annot.type === "signer_date" &&
          (!textTrimmed || ["Date", "Date...", "Select a date"].includes(textTrimmed));
        // Keep desktop/web looking the same, but scale DOWN on mobile because
        // the rendered PDF page width is much smaller there.
        const previewFontSizePx = (() => {
          const base = Number(annot.fontSize || 21);
          const clampedBase = Math.max(10, Math.min(72, base));
          if (!isMobileTextOrDate) return clampedBase;
          // Desktop baseline page width is ~800px in this app (see calculateImageWidth base).
          const baselinePageWidth = 800;
          let factor = pageWidth > 0 ? Math.min(1, pageWidth / baselinePageWidth) : 1;
          // Mobile: scale font down more so it doesn't look too big (extra 0.8 factor + cap at 12px).
          if (isMobile) {
            factor *= 0.8;
            const scaled = Math.max(9, Math.min(72, clampedBase * factor));
            return Math.min(12, scaled); // Cap mobile text/date font at 12px
          }
          return Math.max(10, Math.min(72, clampedBase * factor));
        })();
        // Make the BOX height follow the same "web-like" proportional sizing.
        // Use backend-driven height, but ensure it can fit the chosen font size
        // without creating big empty space (which looks like padding).
        const effectiveAnnotationHeight = (() => {
          if (!isMobileTextOrDate) return annotationHeight;
          const minToFitText = previewFontSizePx * 1.2;
          const h = Math.max(annotationHeight, minToFitText);
          // On mobile: limit growth so boxes don't overlap fields below (max ~25% taller)
          if (isMobile && (annot.type === "signer_text" || annot.type === "signer_date")) {
            const maxH = Math.max(annotationHeight, annotationHeight * 1.25);
            return Math.max(12, Math.min(h, maxH));
          }
          // Keep within sane bounds on mobile for other fields
          return Math.max(14, Math.min(h, 48));
        })();
        // Use pixelY as top so the field doesn't shift vertically (no centering expansion on mobile).
        const effectivePixelY = pixelY;
        // User-requested: remove padding inside mobile fields
        const mobilePadding = "0";

        const mobilePlaceholderText = (() => {
          if (!isMobile) return null;
          if (isTextPlaceholder) {
            if (pixelWidth < 85) return "Text";
            if (pixelWidth < 110) return "Tap to type";
            return "Tap to enter text";
          }
          if (isDatePlaceholder) {
            if (pixelWidth < 85) return "Date";
            if (pixelWidth < 110) return "Tap date";
            return "Tap to select date";
          }
          return null;
        })();

        return (
          <Box
            id={`item-${annot.id}-${pageNum}`}
            key={`${pageNum}-${index}`}
            data-annot-id={`${pageNum}-${annot.id}`}
            onClick={(e) => {
              e.stopPropagation();
              if (shouldLockAspectRatio(annot.type)) {
                setActiveAnnotation(`${pageNum}-${index}`);
              }
            }}
            onTouchStart={(e) => {
              // Stop propagation to prevent TransformWrapper from capturing touch
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              if (shouldLockAspectRatio(annot.type)) {
                setActiveAnnotation(`${pageNum}-${index}`);
              }
            }}
            sx={{
              position: "absolute",
              left: `${pixelX}px`,
              top: `${effectivePixelY}px`,
              width: `${pixelWidth}px`, // Use calculated width (for date fields, this is the minimum width based on text content)
              height: `${effectiveAnnotationHeight}px`,
              display:
                parseInt(signerLoginId) === parseInt(annot.signerId)
                  ? annot.type === "signer_date" ? "inline-block" : "block"
                  : "none",
              transition:
                isDragging || isResizing || isDeleting
                  ? "none"
                  : "transform 0.1s ease-out",
              cursor: "default",
              // Chrome mobile: Allow touch events to pass through to child elements
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
              pointerEvents: "auto",
              // Proper z-index hierarchy to prevent overlaps
              // Text fields get highest priority, then dates/dropdowns, then other annotations
              zIndex: annot.type === "signer_text"
                ? 10000 + (pageNum * 100) + index  // Unique z-index per text field to prevent overlaps
                : annot.type === "signer_date" || annot.type === "signer_dropdown"
                  ? 5000 + (pageNum * 100) + index  // Dates and dropdowns get medium priority
                  : annot.type === "signer_checkmark"
                    ? 3000 + (pageNum * 100) + index  // Checkmarks get lower priority
                    : 1000 + (pageNum * 100) + index,  // Other annotations get lowest priority
              isolation: annot.type === "signer_text" ? "isolate" : "auto",
            }}
          >
            {/* {`item-${annot.id}-${pageNum}`} */}
            {/* {signerLoginId}=={annot.signerId} */}
            <Box
              className="annotation"
              data-type={annot.type}
              sx={{
                // width: `${calculateImageWidth(scale)}%`,
                // minWidth: `${calculateImageWidth(scale)}%`,
                position: "relative",
                display: "flex",
                width: "100%",
                minWidth: "auto",
                height: "100%", // Always use 100% to fill parent container (same as EsignSetupCopy.js)
                cursor:
                  annot.type === "my_text" || annot.type === "date"
                    ? "text"
                    : annot.type === "checkmark" ||
                      annot.type === "signer_checkmark"
                      ? "pointer"
                      : "default",
                // cursor: isDragging ? "move" : "default",
                alignItems: isMobile ? "flex-start" : "center",
                transition:
                  isDragging || isResizing || isDeleting
                    ? "none"
                    : "width 0.1s ease-out",
                padding: isMobile && (annot.type === "signer_text" || annot.type === "signer_date") ? "0px" : (isMobile ? `0px ${2 * scale}px` : `${2 * scale}px`),
                margin: 0,
                border: "none",
                backgroundColor: "transparent",
                boxSizing: "border-box",
                // backgroundColor:
                //   annot.type === "highlight"
                //     ? "transparent"
                //     : "rgba(0, 247, 255, 0.25)",
                // transformOrigin: "top left",
                // transform: `scale(${scale})`,
                "&:hover": {
                  "& .resize-handle": {
                    opacity: 1,
                  },
                },
              }}
            >
              {/* Unified annotation rendering with responsive display */}
              {/* Mobile (both portrait and landscape): Show Typography for dropdown - tappable to open modal */}
              {(annot.type === "signer_dropdown" && isMobile) ? (
                <Typography
                  variant="body2"
                  component="div"
                  onClick={() => handleAnnotClick(pageNum, index, annot.type)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    textAlign: "left",
                    padding: `0px ${5 * scale}px`,
                    paddingTop: `0px`,
                    paddingBottom: `0px`,
                    margin: 0,
                    width: "100%",
                    height: "100%",
                    minWidth: `${80 * scale}px`,
                    fontSize: `${Math.max(10, Math.min(13, annot.fontSize || 12))}px`,
                    lineHeight: 1,
                    backgroundColor: getColorWithNewOpacity(
                      annot.signerColor,
                      annot.isRequired
                    ),
                    borderRadius: `${4 * scale}px`,
                    border: `${1 * scale}px solid rgba(0, 51, 153, 0.3)`,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    fontWeight: annot.text ? 500 : 400,
                    boxSizing: "border-box",
                    "&:hover": {
                      border: `${2 * scale}px solid rgb(0, 51, 153)`,
                    },
                    "&:active": {
                      opacity: 0.8,
                    },
                  }}
                >
                  {annot.text || "Select an option"}
                </Typography>
              ) : null}
              {annot?.type === "signer_text" ? (
                <Box
                  onClick={(e) => {
                    // Don't select if clicking on buttons
                    if (e.target.closest('.MuiIconButton-root')) {
                      return;
                    }
                    e.stopPropagation();
                    setFocusedAnnotation(`${pageNum}-${index}`);
                    handleAnnotationClick(pageNum, index, annot);
                    // Open text entry dialog on click (same as EsignSetupCopy.js)
                    handleEditText(pageNum, index, annot);
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    if (!e.target.closest('.MuiIconButton-root')) {
                      document.body.style.cursor = activeAnnotationId === `${pageNum}-${index}` ? "move" : "default";
                    }
                    if (cursorRef.current) {
                      cursorRef.current.style.display = "none";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    // Don't manage cursor here - let the main moveCursor handler do it
                  }}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: "100%",
                    height: "100%",
                    padding: "0px",
                    margin: "0px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: annot.alignment === "center" ? "center" : annot.alignment === "right" ? "flex-end" : "flex-start",
                    // Mobile: center vertically so fields don't look "too tall"
                    justifyContent: isMobile ? "center" : (annot.alignment === "center" ? "center" : annot.alignment === "right" ? "flex-end" : "flex-start"),
                    overflow: "visible",
                    overflowY: "visible",
                    overflowX: "hidden",
                    cursor: "default",
                    boxSizing: "border-box",
                    gap: 0,
                    fontFamily: annot.fontFamily || "Helvetica, Arial, sans-serif",
                    fontSize: `${previewFontSizePx}px`,
                    color: annot.color || "rgba(0, 0, 0, 0.87)",
                    letterSpacing: annot.letterSpacing || 0,
                    opacity: annot.opacity || 1,
                    backgroundColor: getColorWithNewOpacity(
                      getSignerColorById(annot.signerId, signers),
                      annot.isRequired
                    ),
                    textAlign: annot.alignment || "left",
                    fontWeight: annot.format?.includes("bold")
                      ? "bold"
                      : "normal",
                    fontStyle: annot.format?.includes("italic")
                      ? "italic"
                      : "normal",
                    textDecoration: annot.format?.includes("underline")
                      ? "underline"
                      : "none",
                    border:
                      activeAnnotationId === `${pageNum}-${index}`
                        ? "2px solid rgb(0, 145, 255)"
                        : "1px solid rgba(0, 0, 0, 0.23)",
                    "&:hover": {
                      border: `2px solid ${annot.borderColor || "rgb(0, 145, 255)"}`,
                      backgroundColor: getColorWithNewOpacity(
                        getSignerColorById(annot.signerId, signers),
                        annot.isRequired,
                        0.3
                      ),
                    },
                    transition: "all 0.2s ease",
                    pointerEvents: "auto",
                    zIndex: 9999,
                    isolation: "isolate",
                    // Mobile: Show as clickable box that opens modal
                    ...(isMobile && isMobilePortrait && {
                      cursor: "pointer",
                    }),
                  }}
                >
                  <p
                    style={{
                      width: "100%",
                      height: isMobile && annot.type === "signer_text" ? "auto" : "100%",
                      margin: "0",
                      padding: mobilePadding,
                      display: isMobile ? "block" : undefined,
                      // Desktop signer_text: single line (nowrap + ellipsis) so text doesn't wrap and clip; matches PDF download.
                      overflow:
                        !isMobile && annot.type === "signer_text" ? "hidden" : (isMobile ? "visible" : "visible"),
                      whiteSpace:
                        isMobile && annot.type === "signer_text"
                          ? "pre-wrap"
                          : annot.type === "signer_text"
                            ? "nowrap"
                            : (isMobile ? "nowrap" : "normal"),
                      textOverflow:
                        !isMobile && annot.type === "signer_text" ? "ellipsis" : (isMobile && annot.type === "signer_text" ? undefined : (isMobile ? "ellipsis" : undefined)),
                      wordWrap:
                        isMobile && annot.type === "signer_text" ? "break-word" : (isMobile ? undefined : "break-word"),
                      wordBreak:
                        isMobile && annot.type === "signer_text" ? "break-word" : (isMobile ? undefined : "break-word"),
                      overflowWrap:
                        isMobile && annot.type === "signer_text" ? "anywhere" : (isMobile ? undefined : "break-word"),
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      color:
                        isTextPlaceholder ? "rgba(0, 0, 0, 0.5)" : "inherit",
                      letterSpacing: "inherit",
                      fontWeight: "inherit",
                      fontStyle:
                        isTextPlaceholder ? "italic" : "inherit",
                      textDecoration: "inherit",
                      textAlign: annot.alignment || "left",
                      lineHeight: isMobile ? 1.15 : (annot.lineHeight || 1),
                      boxSizing: "border-box",
                    }}
                  >
                    {isTextPlaceholder
                      ? (mobilePlaceholderText || (isMobile ? "Tap to enter text" : "Click to enter text"))
                      : textRaw}
                  </p>
                </Box>
              ) : null}

              {annot?.type === "signer_checkmark" && (
                <Box
                  sx={{
                    width: pixelWidth,
                    height: isMobile
                      ? (isMobilePortrait ? `${Math.min(28 * scale, Math.max(22 * scale, pixelHeight * 0.5))}px` : `${pixelHeight}px`)
                      : pixelHeight,
                    maxHeight: isMobile ? (isMobilePortrait ? `${28 * scale}px` : `${pixelHeight}px`) : pixelHeight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: getColorWithNewOpacity(
                      annot.signerColor,
                      annot.isRequired
                    ),
                    border:
                      activeAnnotationId === `${pageNum}-${index}`
                        ? `${2 * scale}px solid rgb(0, 51, 153)`
                        : `${1 * scale}px solid rgba(0, 0, 0, 0.23)`,
                    borderRadius: `${4 * scale}px`,
                    cursor: "pointer",
                    "&:hover": {
                      border: `${2 * scale}px solid rgb(0, 51, 153)`,
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnnotations((prev) => {
                      const pageAnnotations = [...(prev[pageNum] || [])];
                      pageAnnotations[index] = {
                        ...pageAnnotations[index],
                        checked: !pageAnnotations[index].checked,
                      };
                      return {
                        ...prev,
                        [pageNum]: pageAnnotations,
                      };
                    });
                    setActiveAnnotationId(`${pageNum}-${index}`);
                  }}
                >
                  {annot.checked ? (
                    <CheckBoxIcon
                      sx={{
                        // width: "100%",
                        // height: "100%",
                        width: isMobile ? "15px" : "100%",
                        height: isMobile ? "15px" : "100%",
                        color: "rgb(0, 51, 153)",
                      }}
                    />
                  ) : (
                    <CheckBoxOutlineBlankIcon
                      sx={{
                        // width: "100%",
                        // height: "100%",
                        width: isMobile ? "15px" : "100%",
                        height: isMobile ? "15px" : "100%",
                        color: "rgba(0, 0, 0, 0.54)",
                      }}
                    />
                  )}
                </Box>
              )}

              {/* Date field - rendered as Box (like text fields) */}
              {annot?.type === "signer_date" ? (
                <Box
                  onClick={(e) => {
                    // Don't select if clicking on buttons
                    if (e.target.closest('.MuiIconButton-root')) {
                      return;
                    }
                    e.stopPropagation();
                    setFocusedAnnotation(`${pageNum}-${index}`);
                    handleAnnotationClick(pageNum, index, annot);
                    // Open date picker dialog on click (same as text fields)
                    handleEditDate(pageNum, index, annot);
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    if (!e.target.closest('.MuiIconButton-root')) {
                      document.body.style.cursor = activeAnnotationId === `${pageNum}-${index}` ? "move" : "default";
                    }
                    if (cursorRef.current) {
                      cursorRef.current.style.display = "none";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    // Don't manage cursor here - let the main moveCursor handler do it
                  }}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: "100%",
                    height: "100%",
                    padding: "0px",
                    margin: "0px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    // Pin date to bottom of box (on printed underline), not vertical center
                    justifyContent: "flex-end",
                    overflow: "visible",
                    overflowY: "visible",
                    overflowX: "hidden",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    gap: 0,
                    fontFamily: annot.fontFamily || "Helvetica, Arial, sans-serif",
                    fontSize: `${previewFontSizePx}px`,
                    color: annot.color || "rgba(0, 0, 0, 0.87)",
                    letterSpacing: annot.letterSpacing || 0,
                    opacity: annot.opacity || 1,
                    backgroundColor: getColorWithNewOpacity(
                      getSignerColorById(annot.signerId, signers),
                      annot.isRequired
                    ),
                    textAlign: annot.alignment || "left",
                    fontWeight: annot.format?.includes("bold")
                      ? "bold"
                      : "normal",
                    fontStyle: annot.format?.includes("italic")
                      ? "italic"
                      : "normal",
                    textDecoration: annot.format?.includes("underline")
                      ? "underline"
                      : "none",
                    border:
                      activeAnnotationId === `${pageNum}-${index}`
                        ? "2px solid rgb(0, 145, 255)"
                        : "1px solid rgba(0, 0, 0, 0.23)",
                    "&:hover": {
                      border: `2px solid ${annot.borderColor || "rgb(0, 145, 255)"}`,
                      backgroundColor: getColorWithNewOpacity(
                        getSignerColorById(annot.signerId, signers),
                        annot.isRequired,
                        0.3
                      ),
                    },
                    transition: "all 0.2s ease",
                    pointerEvents: "auto",
                    zIndex: 9999,
                    isolation: "isolate",
                  }}
                >
                  <p
                    style={{
                      width: "100%",
                      height: isMobile ? "auto" : "100%",
                      margin: "0",
                      padding: mobilePadding,
                      display: isMobile ? "block" : undefined,
                      overflow: isMobile ? "visible" : "visible",
                      // Mobile signer_date: allow wrapping when hitting right edge.
                      whiteSpace: isMobile ? "pre-wrap" : "nowrap",
                      wordWrap: isMobile ? "break-word" : undefined,
                      wordBreak: isMobile ? "break-word" : undefined,
                      overflowWrap: isMobile ? "anywhere" : undefined,
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      color: isDatePlaceholder ? "rgba(0, 0, 0, 0.5)" : "inherit",
                      letterSpacing: "inherit",
                      fontWeight: "inherit",
                      fontStyle: isDatePlaceholder ? "italic" : "inherit",
                      textDecoration: "inherit",
                      textAlign: annot.alignment || "left",
                      lineHeight: isMobile ? 1.15 : (annot.lineHeight || 1),
                      boxSizing: "border-box",
                      textOverflow: isMobile ? undefined : undefined,
                    }}
                  >
                    {isDatePlaceholder
                      ? (mobilePlaceholderText || (isMobile ? "Tap to select date" : "Click to select date"))
                      : textRaw}
                  </p>
                </Box>
              ) : null}

              {[
                "signer_initials",
                "signer_initials_text",
                "signer_chooseImgStamp",
                "signer_chooseImgDrivingL",
                "signer_chooseImgPassportPhoto",
              ].includes(annot.type) && (
                  <>
                    {!annot.imageUrl ? (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusedAnnotation(`${pageNum}-${index}`);
                          handleAnnotationClick(pageNum, index, annot);

                          // #region agent log
                          try {
                            if (isMobile) {
                              const imageElem = document.querySelector(`#img-${pageNum} .pdf-image`);
                              const imgRect = imageElem?.getBoundingClientRect?.();
                              fetch('http://127.0.0.1:7246/ingest/8b376689-945a-4a97-94d5-d387ea1b8602', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: 'debug-session', runId: 'placement-pre', hypothesisId: 'P2', location: 'src/pages/EsignReceivingEndCopy.js:click(upload-placeholder)', message: 'clicked placeholder', data: { pageNum, index, type: annot.type, annotXY: { x: annot.x, y: annot.y }, activePage, isMobile: !!isMobile, pdfZoomScale, client: { x: e?.clientX, y: e?.clientY }, imgRect: imgRect ? { left: imgRect.left, top: imgRect.top, width: imgRect.width, height: imgRect.height } : null }, timestamp: Date.now() }) }).catch(() => { });
                            }
                          } catch (_) { }
                          // #endregion

                          if (isSignatureType(annot.type)) {
                            // Open signature modal
                            setLastClickPosition({ x: annot.x, y: annot.y });
                            handleEditSignature(pageNum, index, annot);
                            setPageNoClicked(pageNum);
                            // setSignatureModalOpen(true);
                          } else if (isImageUploadType(annot.type)) {
                            setPageNoClicked(pageNum);
                            // For stamp-like placeholders, stamp at the placeholder box (web parity).
                            // Also fixes cases where stampClickPosition was never set on mobile.
                            setStampClickPosition({ x: annot.x, y: annot.y });
                            handleEditStamp(pageNum, index, annot);
                          }
                        }}
                        sx={{
                          width: "100%",
                          height: "100%",
                          position: "relative",
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: getColorWithNewOpacity(
                            annot.signerColor,
                            annot.isRequired
                          ),
                          border:
                            activeAnnotationId === `${pageNum}-${index}`
                              ? `${2 * scale}px solid rgb(0, 51, 153)`
                              : `${1 * scale}px solid rgba(0, 51, 153, 0.3)`,
                          borderRadius: `${4 * scale}px`,
                          gap: isMobile ? `${2 * scale}px` : 0.5,
                          padding: isMobile ? `${2 * scale}px` : 0,
                          transition: "all 0.2s ease-out",
                          "&:hover": {
                            backgroundColor: `${selectedSigner?.color}30`,
                          },
                          "& svg": {
                            fontSize: isMobile ? `${12 * scale}px` : "inherit",
                            width: isMobile ? `${12 * scale}px` : "auto",
                            height: isMobile ? `${12 * scale}px` : "auto",
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(0, 0, 0, 0.87)",
                            fontSize: isMobile ? `${Math.max(9, Math.min(12, 10))}px` : `19px`,
                            // fontSize: "21px",
                            textAlign: "center",
                            fontWeight: isMobile ? 500 : "normal",
                            padding: isMobile ? `${2 * scale}px` : 0,
                          }}
                        >
                          {getSignerBoxContent(annot.type)?.text}
                        </Typography>
                        {(annot.type === "signer_initials" ||
                          annot.type === "signer_initials_text") && (
                            <Box
                              sx={{
                                width: "85%",
                                height: isMobile ? `${2 * scale}px` : `${1 * scale}px`,
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                marginTop: isMobile ? `${6 * scale}px` : `${5 * scale}px`,
                              }}
                            />
                          )}
                      </Box>
                    ) : (
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusedAnnotation(`${pageNum}-${index}`);
                          handleAnnotationClick(pageNum, index, annot);
                        }}
                        sx={{
                          width: "100%",
                          height: "100%",
                          position: "relative",
                          cursor: "pointer",
                          backgroundColor: getColorWithNewOpacity(
                            annot.signerColor,
                            annot.isRequired
                          ),
                          border:
                            activeAnnotationId === `${pageNum}-${index}`
                              ? `${2 * scale}px solid rgb(0, 51, 153)`
                              : `${1 * scale}px solid rgba(0, 51, 153, 0.3)`,
                          borderRadius: `${4 * scale}px`,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {loadingAnnotations.has(`${pageNum}-${index}`) && (
                          <CircularProgress
                            size={isMobile ? 16 : 24}
                            sx={{
                              position: "absolute",
                              zIndex: 1,
                              color: "rgba(0, 51, 153, 0.8)",
                            }}
                          />
                        )}
                        <img
                          src={annot.imageUrl}
                          alt="Uploaded content"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "auto",
                            height: "auto",
                            objectFit: "contain",
                            opacity: loadingAnnotations.has(`${pageNum}-${index}`) ? 0.5 : 1,
                            transition: "opacity 0.3s ease",
                          }}
                        />
                      </Box>
                    )}{" "}
                  </>
                )}
              {annot.type === "signer_dropdown" && (
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  value={annot.selectedOption || ""} // Controlled value from annotation
                  onChange={(e) => {
                    const newOption = e.target.value;
                    setAnnotations((prev) => ({
                      ...prev,
                      [pageNum]: prev[pageNum].map((a, i) =>
                        i === index ? { ...a, selectedOption: newOption } : a
                      ),
                    }));
                  }}
                  placeholder="Select..."
                  onClick={(e) => {
                    e.stopPropagation();
                    setFocusedAnnotation(`${pageNum}-${index}`);
                    handleAnnotationClick(pageNum, index, annot);
                  }}
                  onBlur={() => {
                    setFocusedAnnotation(null);
                  }}
                  onMouseEnter={() => {
                    document.body.style.cursor = "default";
                    if (cursorRef.current) {
                      cursorRef.current.style.display = "none";
                    }
                  }}
                  onMouseLeave={() => {
                    if (isAnnotating && !focusedAnnotation) {
                      document.body.style.cursor = "none";
                      if (cursorRef.current) {
                        cursorRef.current.style.display = "block";
                      }
                    }
                  }}
                  sx={{
                    display: !isMobile ? "block" : "none",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: getColorWithNewOpacity(
                        getSignerColorById(annot.signerId, signers),
                        annot.isRequired
                      ),
                      width: pixelWidth,
                      height: pixelHeight,
                      cursor: "pointer !important", // Should be pointer since user can select
                      "& .MuiSelect-select": {
                        cursor: "pointer !important",
                      },
                      "& .MuiOutlinedInput-input": {
                        cursor: "pointer !important",
                      },
                      border:
                        activeAnnotationId === `${pageNum}-${index}`
                          ? `${2 * scale}px solid rgb(0, 51, 153)`
                          : `${1 * scale}px solid rgba(0, 0, 0, 0.23)`,
                      "&:hover": {
                        border: `${2 * scale}px solid ${annot.borderColor || "rgb(0, 51, 153)"
                          }`,
                        cursor: "pointer !important",
                      },
                      "& *": {
                        cursor: "pointer !important",
                      },
                    },
                  }}
                >
                  {annot.options && annot.options.length > 0 ? (
                    annot.options.map((option, idx) => (
                      <MenuItem key={idx} value={option}>
                        {option}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No options</MenuItem>
                  )}
                </TextField>
              )}

              {(annot.type === "signer_initials" ||
                annot.type === "signer_chooseImgStamp" ||
                annot.type === "signer_chooseImgDrivingL" ||
                annot.type === "signer_chooseImgPassportPhoto" ||
                annot.type === "signer_initials_text") &&
                annot.imageUrl ? (
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: `${-15 * scale}px`,
                    right: `${5 * scale}px`,
                    backgroundColor: "rgb(35, 179, 232)",
                    padding: `${3 * scale}px`,
                    zIndex: 1000,
                    cursor: "pointer !important",
                    "&:hover": {
                      backgroundColor: "rgb(2 142 193)", // Darker red on hover
                      cursor: "pointer !important",
                    },
                  }}
                  disableRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    if (
                      annot.type === "signer_initials" ||
                      annot.type === "signer_initials_text"
                    ) {
                      handleEditSignature(pageNum, index, annot);
                    } else if (
                      annot.type === "signer_chooseImgStamp" ||
                      annot.type === "signer_chooseImgPassportPhoto" ||
                      annot.type === "signer_chooseImgDrivingL"
                    ) {
                      handleEditStamp(pageNum, index, annot);
                    }
                  }}
                  onMouseDown={(e) => e.stopPropagation()} // Prevent drag events
                  onMouseUp={(e) => e.stopPropagation()}
                >
                  <EditIcon
                    sx={{
                      color: "white",
                      fontSize: `${15 * scale}px`,
                      pointerEvents: "none",
                    }}
                  />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [annotations, isAnnotating, focusedAnnotation, scale, isInteracting, imageResizeKey, isMobile, isMobileLandscape, isMobilePortrait, calculateDateWidth, activeAnnotationId, signerLoginId, signers, pdfZoomScale]
  );
  const mainContainerClass = isAnnotating ? "custom-cursor" : "";
  // Update the useEffect for cursor handling

  useEffect(() => {
    if (isAnnotating) {
      const cursorElement = document.createElement("div");
      cursorElement.classList.add("custom-cursor-image");
      document.body.appendChild(cursorElement);
      cursorRef.current = cursorElement;

      const moveCursor = (e) => {
        const target = e.target;
        const isTextField =
          target.tagName === "INPUT" || target.closest(".MuiTextField-root");
        const isImageAnnotation =
          target.closest(".annotation") &&
          target.closest(".annotation").querySelector("img");
        const isResizeHandle = false; // Removed Rnd component, no resize handles
        const isPdfContainer = target.closest(".pdf-container");
        const isSidebar =
          target.closest(".left-sidebar") ||
          target.closest(".right-sidebar") ||
          target.closest(".zoom-container");
        // Add checks for edit and delete icons
        const isEditIcon =
          target.closest('[aria-label="edit"]') ||
          target.closest(".MuiIconButton-root");
        const isDeleteIcon = target.closest('[aria-label="close"]');
        const isActionIcon = isEditIcon || isDeleteIcon;

        // Hide custom cursor and show pointer for action icons
        if (isActionIcon) {
          cursorRef.current.style.display = "none";
          document.body.style.cursor = "pointer";
          return;
        }

        // Rest of your existing cursor logic
        if (isImageAnnotation) {
          cursorRef.current.style.display = "block";
          cursorRef.current.style.left = `${e.clientX}px`;
          cursorRef.current.style.top = `${e.clientY}px`;
          document.body.style.cursor = "none";
          return;
        }

        if (isResizeHandle || isSidebar) {
          cursorRef.current.style.display = "none";
          document.body.style.cursor = "default";
          return;
        }

        if (isPdfContainer && !isTextField) {
          cursorRef.current.style.display = "block";
          cursorRef.current.style.left = `${e.clientX}px`;
          cursorRef.current.style.top = `${e.clientY}px`;
          document.body.style.cursor = "none";
        } else {
          cursorRef.current.style.display = "none";
          document.body.style.cursor = isTextField ? "text" : "default";
        }
      };

      document.addEventListener("mousemove", moveCursor);
      return () => {
        document.removeEventListener("mousemove", moveCursor);
        if (cursorRef.current?.parentNode) {
          document.body.removeChild(cursorRef.current);
          cursorRef.current = null;
        }
        document.body.style.cursor = "default";
      };
    }
  }, [isAnnotating]);

  // In your App component
  const [isDownloading, setIsDownloading] = useState(false);
  // const [isDownloading, setIsDownloading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeAnnot, setActiveAnnot] = useState(null);
  const [inputText, setInputText] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const mobileInputRef = useRef(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Text entry dialog state (same as EsignSetupCopy.js)
  const [textEntryDialogOpen, setTextEntryDialogOpen] = useState(false);
  const [pendingTextAnnotation, setPendingTextAnnotation] = useState(null);
  const [textEntryInitialText, setTextEntryInitialText] = useState("");

  // Date picker dialog state
  const [datePickerDialogOpen, setDatePickerDialogOpen] = useState(false);
  const [pendingDateAnnotation, setPendingDateAnnotation] = useState(null);
  const [datePickerSelectedDate, setDatePickerSelectedDate] = useState(null);

  const handleDownloadOriginal = async () => {
    if (!originalPdf) return;

    setIsDownloading(true);
    try {
      // Download annotated PDF (receiver-side) like EsignSetupCopy.js
      // Include only current signer's filled fields (plus any "my_" fields if present)
      const allowedTypes = [
        "my_text",
        "signer_text",
        "date",
        "signer_date",
        "signer_dropdown",
        "my_dropdown",
        "my_signature",
        "my_initials",
        "stamp",
        "highlight",
        "checkmark",
        "signer_checkmark",
        "signer_initials",
        "signer_initials_text",
        "signer_chooseImgStamp",
        "signer_chooseImgDrivingL",
        "signer_chooseImgPassportPhoto",
      ];

      const filteredAnnotations = {};
      Object.entries(annotations || {}).forEach(([pageNum, pageAnnotations]) => {
        const arr = Array.isArray(pageAnnotations) ? pageAnnotations : [];
        let onlyMine = arr.filter((annot) => {
          if (!annot || !allowedTypes.includes(annot.type)) return false;
          if (annot.type && String(annot.type).startsWith("my_")) return true;
          const sid = annot.signerId ?? annot.signer_id_receive ?? annot.signer_id;
          if (sid === undefined || sid === null || sid === "") return false;
          return parseInt(sid, 10) === parseInt(signerLoginId, 10);
        });

        // On mobile: scale TEXT field widths so PDF matches web (text fields were sized for narrow mobile screen)
        // Note: x, y percentages don't need scaling - they're already correct relative to page dimensions
        // Note: Image annotations (signatures, initials) should NOT be scaled - their percentages are already correct
        if (isMobile && onlyMine.length > 0) {
          const pageEl = document.querySelector(`#img-${pageNum} .pdf-image`);
          if (pageEl) {
            // Use layout dimensions (not getBoundingClientRect) to match display logic
            const layoutW = pageEl.offsetWidth || pageEl.clientWidth || 0;
            const actualPageWidth = layoutW > 0 ? layoutW : (pageEl.getBoundingClientRect()?.width || 0);
            const baselinePageWidth = 800;
            if (actualPageWidth > 0 && actualPageWidth < baselinePageWidth) {
              const scaleFactor = baselinePageWidth / actualPageWidth;
              // Only text types need width scaling (they were sized for narrow mobile screen)
              const textTypes = ["signer_text", "signer_date", "my_text", "date"];

              onlyMine = onlyMine.map((annot) => {
                const isTextType = textTypes.includes(annot.type);

                if (isTextType) {
                  // Only scale width for text fields
                  return {
                    ...annot,
                    width: Math.min(100, annot.width * scaleFactor),
                  };
                }
                // Image annotations (signatures, initials, stamps) - don't scale, percentages are correct
                return annot;
              });
              console.log(`[Download] Mobile: scaled text field widths by ${scaleFactor.toFixed(2)}x (page ${actualPageWidth}px -> ${baselinePageWidth}px baseline)`);
            }
          }
        }

        if (onlyMine.length > 0) {
          filteredAnnotations[pageNum] = onlyMine;
        }
      });

      // Use actual rendered page width so downloaded font size matches editor (A4, A3, Letter, etc.).
      // On mobile, the viewport is narrow so the measured width would make PDF fonts too large; use
      // the same baseline (800) as desktop so mobile-signed docs get correct font size in the PDF.
      const baselinePageWidth = 800;
      let editorPageWidthPx = null;
      if (isMobile) {
        editorPageWidthPx = baselinePageWidth;
        if (editorPageWidthPx != null) {
          console.log("[Download] Mobile: using baseline page width for font scaling (px):", editorPageWidthPx);
        }
      } else {
        const firstPageWithAnnots = Object.keys(filteredAnnotations)[0];
        if (firstPageWithAnnots) {
          const pageEl = document.querySelector(`#img-${firstPageWithAnnots} .pdf-image`);
          if (pageEl) {
            // Use layout dimensions (not getBoundingClientRect) to match display logic
            const layoutW = pageEl.offsetWidth || pageEl.clientWidth || 0;
            if (layoutW > 0) {
              editorPageWidthPx = layoutW;
            } else {
              const rect = pageEl.getBoundingClientRect();
              if (rect?.width > 0) editorPageWidthPx = rect.width;
            }
          }
        }
        if (editorPageWidthPx != null) {
          console.log("[Download] Using editor page width for font scaling (px):", Math.round(editorPageWidthPx));
        }
      }

      if (Object.keys(filteredAnnotations).length > 0) {
        const success = await downloadPdfWithTextAnnotations(
          originalPdf,
          filteredAnnotations,
          fileName || "document.pdf",
          {
            editorPageWidthPx: editorPageWidthPx ?? undefined,
            logFontScale: true,
            textVerticalNudgeUpPoints: -5,
          }
        );
        if (!success) {
          toastAlert("error", "Failed to download PDF with annotations");
        }
      } else {
        const success = await downloadOriginalPdf(originalPdf, fileName);
        if (!success) {
          toastAlert("error", "Failed to download PDF");
        }
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toastAlert("error", "Failed to download PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  const blurActiveElementSafely = () => {
    try {
      const el = document?.activeElement;
      if (el && typeof el.blur === "function") el.blur();
    } catch (_) {
      // no-op
    }
  };

  const handleAnnotClick = (pageNum, index, type) => {
    // Prevent Chrome from blocking MUI's aria-hidden updates on mobile
    // by ensuring no element in the background retains focus when opening a dialog.
    blurActiveElementSafely();
    setActiveAnnot({ pageNum, index });
    setInputValue(annotations[pageNum][index]?.text || "");
    setAnnotationType(type);
    if (type === "signer_dropdown" && annotations[pageNum][index]?.options) {
      setDropdownOptions(annotations[pageNum][index].options);
    }

    setModalOpen(true);
  };

  // Handler for editing text fields (same as EsignSetupCopy.js)
  const handleEditText = (pageNum, index, annot) => {
    blurActiveElementSafely();
    setPendingTextAnnotation({ pageNum, index });
    setTextEntryInitialText(annot?.text || "");
    setTextEntryDialogOpen(true);
  };

  // Handler to cancel text entry
  const handleTextEntryCancel = () => {
    setTextEntryDialogOpen(false);
    setPendingTextAnnotation(null);
    setTextEntryInitialText("");
  };

  // Handler for editing date fields
  const handleEditDate = (pageNum, index, annot) => {
    blurActiveElementSafely();
    setPendingDateAnnotation({ pageNum, index });
    // Parse the current date text to Date object
    let initialDate = null;
    if (annot?.text && annot.text !== "Date" && annot.text !== "Date..." && annot.text !== "Select a date") {
      try {
        const dateFormat = annot.dateFormat || "MMM dd, yyyy";
        const parsedDate = parse(annot.text, dateFormat, new Date());
        if (isValid(parsedDate)) {
          initialDate = parsedDate;
        } else {
          const directDate = new Date(annot.text);
          if (isValid(directDate)) {
            initialDate = directDate;
          }
        }
      } catch (error) {
        // If parsing fails, leave as null
      }
    }
    setDatePickerSelectedDate(initialDate); // Set selected date for the picker
    setDatePickerDialogOpen(true);
  };

  // Handler to cancel date picker
  const handleDatePickerCancel = () => {
    setDatePickerDialogOpen(false);
    setPendingDateAnnotation(null);
    setDatePickerSelectedDate(null);
  };

  // Handler to complete date selection and update annotation
  const handleDatePickerComplete = (selectedDate) => {
    if (!pendingDateAnnotation || !selectedDate) {
      handleDatePickerCancel();
      return;
    }

    const { pageNum, index } = pendingDateAnnotation;
    const currentAnnotation = annotations[pageNum]?.[index];
    if (!currentAnnotation) {
      handleDatePickerCancel();
      return;
    }

    const dateFormat = currentAnnotation.dateFormat || "MMM dd, yyyy";
    const formattedDate = format(selectedDate, dateFormat);

    // Get page element for dimension calculations
    const pageElement = document.querySelector(`#img-${pageNum} .pdf-image`);
    if (!pageElement) {
      // Fallback: just update text without recalculating dimensions
      setAnnotations((prev) => {
        const pageAnnotations = [...(prev[pageNum] || [])];
        pageAnnotations[index] = {
          ...pageAnnotations[index],
          text: formattedDate,
        };
        return { ...prev, [pageNum]: pageAnnotations };
      });
      handleDatePickerCancel();
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();
    const pageWidthPx = pageRect.width;
    const pageHeightPx = pageRect.height;

    // Get font properties from annotation
    const fontSize = currentAnnotation.fontSize || 21;
    const fontFamilyFull = currentAnnotation.fontFamily || "Helvetica, Arial, sans-serif";
    // Extract first font from font stack for calculateTextWidth
    const fontFamily = fontFamilyFull.split(",")[0].trim();
    const measuredFontSize = (() => {
      const base = Number(fontSize || 21);
      const clampedBase = Math.max(10, Math.min(72, base));
      if (!isMobile) return clampedBase;
      const baselinePageWidth = 800;
      const factor = pageWidthPx > 0 ? Math.min(1, pageWidthPx / baselinePageWidth) : 1;
      return Math.max(10, Math.min(72, clampedBase * factor));
    })();
    const lineHeight = isMobile ? 1.15 : (currentAnnotation.lineHeight || 1);

    // Calculate width automatically based on actual date text (same as text fields)
    let calculatedWidthPx = calculateTextWidth(formattedDate, measuredFontSize, fontFamily);

    // If text is empty or very short, use a small default
    if (calculatedWidthPx === 0 || calculatedWidthPx < 20) {
      calculatedWidthPx = 20; // Minimum for empty text
    }

    // Add buffer to prevent text cutoff on the right side
    const bufferPx = isMobile ? 8 : 20; // smaller on mobile
    const boxWidthPx = calculatedWidthPx + bufferPx;

    // Calculate maximum allowed width (PDF container width minus current X position)
    const currentXPercent = currentAnnotation.x || 0;
    const currentXPx = (currentXPercent / 100) * pageWidthPx;
    const maxAllowedWidthPx = pageWidthPx - currentXPx;

    // If text width exceeds container, use max width
    // Otherwise, use text width with buffer
    const finalWidthPx = Math.max(0, Math.min(boxWidthPx, maxAllowedWidthPx));
    const widthPercent = pageWidthPx > 0 ? (finalWidthPx / pageWidthPx) * 100 : currentAnnotation.width;

    // On mobile, allow wrapping and grow upward from the bottom (keep bottom fixed).
    let heightPercent = currentAnnotation.height;
    let adjustedYPercent = currentAnnotation.y;
    if (isMobile && pageHeightPx > 0) {
      const textHeightPx = calculateTextHeight(formattedDate, measuredFontSize, fontFamilyFull, finalWidthPx, lineHeight);
      const currentBottomPercent = currentAnnotation.y + currentAnnotation.height;
      const currentBottomPx = (currentBottomPercent / 100) * pageHeightPx;
      const maxAllowedHeightPx = Math.max(0, currentBottomPx);
      const finalHeightPx = Math.max(0, Math.min(textHeightPx, maxAllowedHeightPx));
      heightPercent = (finalHeightPx / pageHeightPx) * 100;
      adjustedYPercent = Math.max(0, currentBottomPercent - heightPercent);
      // if we hit the top, clamp height to remaining space up to bottom
      if (adjustedYPercent === 0) heightPercent = Math.max(0, currentBottomPercent);
    }

    // Update annotation with date and calculated width
    setAnnotations((prev) => {
      const pageAnnotations = [...(prev[pageNum] || [])];
      pageAnnotations[index] = {
        ...pageAnnotations[index],
        text: formattedDate,
        width: widthPercent, // Width adjusts automatically based on date text
        ...(isMobile ? { height: heightPercent, y: adjustedYPercent } : null),
      };
      return { ...prev, [pageNum]: pageAnnotations };
    });

    handleDatePickerCancel();
  };

  // Handler to complete text entry and update annotation
  const handleTextEntryComplete = (enteredText) => {
    if (!pendingTextAnnotation) {
      handleTextEntryCancel();
      return;
    }

    // Enforce 500 character limit for signer_text fields
    const MAX_CHARACTERS = 500;
    const truncatedText = enteredText.length > MAX_CHARACTERS
      ? enteredText.substring(0, MAX_CHARACTERS)
      : enteredText;

    const { pageNum, index } = pendingTextAnnotation;
    const currentAnnotation = annotations[pageNum]?.[index];
    if (!currentAnnotation) {
      handleTextEntryCancel();
      return;
    }

    // Get page element for dimension calculations
    const pageElement = document.querySelector(`#img-${pageNum} .pdf-image`);
    if (!pageElement) {
      // Fallback: just update text without recalculating dimensions
      setAnnotations((prev) => {
        const pageAnnotations = [...(prev[pageNum] || [])];
        pageAnnotations[index] = {
          ...pageAnnotations[index],
          text: truncatedText,
        };
        return { ...prev, [pageNum]: pageAnnotations };
      });
      handleTextEntryCancel();
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();
    const pageWidthPx = pageRect.width;
    const pageHeightPx = pageRect.height;

    // Get font properties from annotation
    const fontSize = currentAnnotation.fontSize || 21;
    const fontFamilyFull = currentAnnotation.fontFamily || "Helvetica, Arial, sans-serif";
    // Extract first font from font stack for calculateTextWidth (same as EsignSetupCopy.js)
    const fontFamily = fontFamilyFull.split(",")[0].trim();
    // Mobile preview scales font down based on page width; match that for sizing math
    const measuredFontSize = (() => {
      const base = Number(fontSize || 21);
      const clampedBase = Math.max(10, Math.min(72, base));
      if (!isMobile) return clampedBase;
      const baselinePageWidth = 800;
      const factor = pageWidthPx > 0 ? Math.min(1, pageWidthPx / baselinePageWidth) : 1;
      return Math.max(10, Math.min(72, clampedBase * factor));
    })();
    const lineHeight = isMobile ? 1.15 : (currentAnnotation.lineHeight || 1);

    // Use truncated text for calculations
    const textToUse = truncatedText;

    // Calculate maximum allowed width (PDF container width minus current X position)
    const currentXPercent = currentAnnotation.x || 0;
    const currentXPx = (currentXPercent / 100) * pageWidthPx;
    const maxAllowedWidthPx = pageWidthPx - currentXPx;

    // Web-like behavior (also on mobile):
    // - Grow width based on text length
    // - If it would hit the page right edge, cap width to remaining space and wrap to next line
    let calculatedWidthPx = calculateTextWidth(textToUse, measuredFontSize, fontFamily);

    // If text is empty or very short, use a small default
    if (calculatedWidthPx === 0 || calculatedWidthPx < 20) {
      calculatedWidthPx = 20; // Minimum for empty text
    }

    // Add buffer to prevent text cutoff on the right side
    const bufferPx = 20; // Extra breathing space on right side
    const boxWidthPx = calculatedWidthPx + bufferPx;

    const finalWidthPx = Math.max(0, Math.min(boxWidthPx, maxAllowedWidthPx));
    const widthPercent = pageWidthPx > 0 ? (finalWidthPx / pageWidthPx) * 100 : (currentAnnotation.width || 0);

    // Calculate height - if text fits on one line, height is single line; otherwise wrap
    const textHeightPx = calculateTextHeight(
      textToUse,
      measuredFontSize,
      fontFamilyFull,
      finalWidthPx,
      lineHeight
    );
    let heightPercent = pageHeightPx > 0 ? (textHeightPx / pageHeightPx) * 100 : (currentAnnotation.height || 0);

    const currentBottomPercent = currentAnnotation.y + currentAnnotation.height;
    let adjustedYPercent = currentAnnotation.y;
    if (isMobile) {
      // Mobile: keep the BOTTOM fixed and grow upward (like web behavior)
      const currentBottomPx = (currentBottomPercent / 100) * pageHeightPx;
      const maxAllowedHeightPx = Math.max(0, currentBottomPx); // can't grow above top
      const finalHeightPx = Math.max(0, Math.min(textHeightPx, maxAllowedHeightPx));
      heightPercent = pageHeightPx > 0 ? (finalHeightPx / pageHeightPx) * 100 : heightPercent;
      adjustedYPercent = Math.max(0, currentBottomPercent - heightPercent);
      // If we hit the top, clamp height to the remaining space up to bottom.
      if (adjustedYPercent === 0) {
        heightPercent = Math.max(0, currentBottomPercent);
      }
    } else {
      // Desktop: resize from bottom to keep bottom position fixed
      const newHeightPx = (heightPercent / 100) * pageHeightPx;
      const currentBottomPx = (currentBottomPercent / 100) * pageHeightPx;
      adjustedYPercent = ((currentBottomPx - newHeightPx) / pageHeightPx) * 100;
      adjustedYPercent = Math.max(0, adjustedYPercent);
    }

    // Update annotation with text and calculated dimensions
    setAnnotations((prev) => {
      const pageAnnotations = [...(prev[pageNum] || [])];
      pageAnnotations[index] = {
        ...pageAnnotations[index],
        text: truncatedText, // Use truncated text if it exceeded limit
        width: widthPercent, // Width adjusts automatically based on text
        height: heightPercent, // Height adjusts based on wrapped text
        y: adjustedYPercent, // Desktop keeps bottom fixed; mobile stays top-anchored
      };
      return { ...prev, [pageNum]: pageAnnotations };
    });
    handleTextEntryCancel();
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setAnnotationType(null);
  };

  const handleSaveInput = () => {
    if (activeAnnot) {
      const { pageNum, index } = activeAnnot;
      const currentAnnotation = annotations[pageNum]?.[index];
      if (!currentAnnotation) {
        handleCloseModal();
        return;
      }

      // For date fields, recalculate width based on date text (same as text fields)
      if (currentAnnotation.type === "signer_date" && inputValue) {
        // Get page element for dimension calculations
        const pageElement = document.querySelector(`#img-${pageNum} .pdf-image`);
        if (pageElement) {
          const pageRect = pageElement.getBoundingClientRect();
          const pageWidthPx = pageRect.width;
          const pageHeightPx = pageRect.height;

          // Get font properties from annotation
          const fontSize = currentAnnotation.fontSize || 21;
          const fontFamilyFull = currentAnnotation.fontFamily || "Helvetica, Arial, sans-serif";
          // Extract first font from font stack for calculateTextWidth
          const fontFamily = fontFamilyFull.split(",")[0].trim();
          const measuredFontSize = (() => {
            const base = Number(fontSize || 21);
            const clampedBase = Math.max(10, Math.min(72, base));
            if (!isMobile) return clampedBase;
            const baselinePageWidth = 800;
            const factor = pageWidthPx > 0 ? Math.min(1, pageWidthPx / baselinePageWidth) : 1;
            return Math.max(10, Math.min(72, clampedBase * factor));
          })();
          const lineHeight = isMobile ? 1.15 : (currentAnnotation.lineHeight || 1);

          // Calculate width automatically based on actual date text
          let calculatedWidthPx = calculateTextWidth(inputValue, measuredFontSize, fontFamily);

          // If text is empty or very short, use a small default
          if (calculatedWidthPx === 0 || calculatedWidthPx < 20) {
            calculatedWidthPx = 20; // Minimum for empty text
          }

          // Add buffer to prevent text cutoff on the right side
          const bufferPx = isMobile ? 8 : 20; // smaller on mobile
          const boxWidthPx = calculatedWidthPx + bufferPx;

          // Calculate maximum allowed width (PDF container width minus current X position)
          const currentXPercent = currentAnnotation.x || 0;
          const currentXPx = (currentXPercent / 100) * pageWidthPx;
          const maxAllowedWidthPx = pageWidthPx - currentXPx;

          // If text width exceeds container, use max width
          // Otherwise, use text width with buffer
          const finalWidthPx = Math.max(0, Math.min(boxWidthPx, maxAllowedWidthPx));
          const widthPercent = pageWidthPx > 0 ? (finalWidthPx / pageWidthPx) * 100 : currentAnnotation.width;

          // Mobile: allow wrapping and grow upward from bottom
          let heightPercent = currentAnnotation.height;
          let adjustedYPercent = currentAnnotation.y;
          if (isMobile && pageHeightPx > 0) {
            const textHeightPx = calculateTextHeight(inputValue, measuredFontSize, fontFamilyFull, finalWidthPx, lineHeight);
            const currentBottomPercent = currentAnnotation.y + currentAnnotation.height;
            const currentBottomPx = (currentBottomPercent / 100) * pageHeightPx;
            const maxAllowedHeightPx = Math.max(0, currentBottomPx);
            const finalHeightPx = Math.max(0, Math.min(textHeightPx, maxAllowedHeightPx));
            heightPercent = (finalHeightPx / pageHeightPx) * 100;
            adjustedYPercent = Math.max(0, currentBottomPercent - heightPercent);
            if (adjustedYPercent === 0) heightPercent = Math.max(0, currentBottomPercent);
          }

          // Update annotation with text and calculated width
          setAnnotations((prev) => {
            const updatedAnnotations = { ...prev };
            updatedAnnotations[pageNum][index] = {
              ...updatedAnnotations[pageNum][index],
              text: inputValue,
              width: widthPercent, // Width adjusts automatically based on date text
              ...(isMobile ? { height: heightPercent, y: adjustedYPercent } : null),
            };
            return updatedAnnotations;
          });
        } else {
          // Fallback: just update text without recalculating dimensions
          setAnnotations((prev) => {
            const updatedAnnotations = { ...prev };
            updatedAnnotations[pageNum][index].text = inputValue;
            return updatedAnnotations;
          });
        }
      } else {
        // For non-date fields, just update text
        setAnnotations((prev) => {
          const updatedAnnotations = { ...prev };
          updatedAnnotations[pageNum][index].text = inputValue;
          return updatedAnnotations;
        });
      }
      handleCloseModal();
    }
  };

  const handleSaveText = () => {
    if (activeAnnot) {
      const { pageNum, index } = activeAnnot;
      setAnnotations((prev) => {
        const updatedAnnotations = { ...prev };
        updatedAnnotations[pageNum][index].text = inputText;
        return updatedAnnotations;
      });
      setModalOpen(false);
    }
  };
  const [maxChars, setMaxChars] = useState(50); // Default max length

  useEffect(() => {
    if (modalOpen && annotationType === "signer_text") {
      if (isMobile && isMobilePortrait) {
        // For mobile portrait: Set default maxChars and focus the input (floating iOS-style input bar)
        setMaxChars(100); // Default reasonable limit for mobile
        // Use multiple attempts to ensure keyboard appears
        const focusInput = () => {
          if (mobileInputRef.current) {
            // inputRef gives us the input element directly
            const input = mobileInputRef.current;
            if (input) {
              // Scroll input into view in portrait mode
              input.scrollIntoView({ behavior: 'smooth', block: 'start' });
              input.focus();
              // Force click to trigger keyboard on iOS
              input.click();
              // Additional focus to ensure keyboard appears
              setTimeout(() => {
                input.focus();
                // Force focus again for portrait mode
                if (document.activeElement !== input) {
                  input.focus();
                  input.click();
                }
              }, 50);
            }
          }
        };
        // Immediate focus
        setTimeout(focusInput, 0);
        // Delayed focus as backup
        setTimeout(focusInput, 100);
        setTimeout(focusInput, 300);
        setTimeout(focusInput, 500);
      } else {
        // For desktop: Calculate max chars
        setTimeout(() => {
          const inputEl = document.querySelector(".MuiDialogContent-root input");
          if (!inputEl) return;

          // Get the font styles
          const computedStyle = window.getComputedStyle(inputEl);

          // Create a hidden span to measure text width
          const span = document.createElement("span");
          span.style.cssText = `
            visibility: hidden;
            position: fixed;
            font-family: ${computedStyle.fontFamily};
            font-size: ${computedStyle.fontSize};
            font-weight: ${computedStyle.fontWeight};
            font-style: ${computedStyle.fontStyle};
            letter-spacing: ${computedStyle.letterSpacing};
            white-space: pre;
            padding: ${computedStyle.padding};
          `;

          document.body.appendChild(span);

          // Available width for text input
          const containerWidth = inputEl.offsetWidth - 8; // Account for padding
          let allowedChars = 0;

          // Incrementally add characters until it exceeds width
          for (let i = 1; i <= 100; i++) {
            span.innerText = "W".repeat(i); // Use a wide character
            if (span.getBoundingClientRect().width > containerWidth) break;
            allowedChars = i;
          }

          document.body.removeChild(span);
          setMaxChars(allowedChars);
        }, 100);
      }
    }
  }, [modalOpen, annotationType, isMobile, isMobileLandscape, isMobilePortrait]);

  // Function to enforce max length
  const handleDialogInputChange = (e) => {
    if (e.target.value.length <= maxChars) {
      setInputValue(e.target.value);
    }
  };

  // Detect keyboard height for mobile floating input (iOS style)
  useEffect(() => {
    if (!isMobile || !isMobilePortrait || !modalOpen || annotationType !== "signer_text") {
      setKeyboardHeight(0);
      return;
    }

    let lastKeyboardHeight = 0;
    const windowHeight = window.innerHeight;

    const updateKeyboardHeight = () => {
      if (window.visualViewport) {
        // Use Visual Viewport API - position directly above keyboard
        // When keyboard is open, visualViewport.height is smaller
        const viewportHeight = window.visualViewport.height;
        const viewportTop = window.visualViewport.offsetTop || 0;
        // Calculate the bottom position of the visible viewport
        // This is where the keyboard starts
        const viewportBottom = viewportTop + viewportHeight;
        // The keyboard height is the difference from window bottom to viewport bottom
        const calculatedKeyboardHeight = window.innerHeight - viewportBottom;
        const newHeight = Math.max(0, Math.round(calculatedKeyboardHeight));

        // Update if there's a change (even small changes)
        if (Math.abs(newHeight - lastKeyboardHeight) > 1) {
          setKeyboardHeight(newHeight);
          lastKeyboardHeight = newHeight;
        }
      } else {
        // Fallback: detect via window innerHeight changes
        const currentHeight = window.innerHeight;
        const calculatedKeyboardHeight = windowHeight - currentHeight;
        const newHeight = Math.max(0, Math.round(calculatedKeyboardHeight));

        if (Math.abs(newHeight - lastKeyboardHeight) > 1) {
          setKeyboardHeight(newHeight);
          lastKeyboardHeight = newHeight;
        }
      }
    };

    // Initial check with delay to allow viewport to settle
    setTimeout(updateKeyboardHeight, 50);

    // Also listen to focus/blur on input to detect keyboard immediately
    const handleInputFocus = () => {
      // Check immediately and then with delays to catch keyboard animation
      updateKeyboardHeight();
      setTimeout(updateKeyboardHeight, 50);
      setTimeout(updateKeyboardHeight, 150);
      setTimeout(updateKeyboardHeight, 300);
      setTimeout(updateKeyboardHeight, 500);
    };
    const handleInputBlur = () => {
      setTimeout(() => {
        setKeyboardHeight(0);
        lastKeyboardHeight = 0;
      }, 200);
    };

    const inputElement = mobileInputRef.current;
    if (inputElement) {
      inputElement.addEventListener("focus", handleInputFocus);
      inputElement.addEventListener("blur", handleInputBlur);
    }

    // Listen to visual viewport changes (iOS Safari, Chrome mobile)
    let rafId;
    if (window.visualViewport) {
      const handleViewportResize = () => {
        updateKeyboardHeight();
      };
      const handleViewportScroll = () => {
        updateKeyboardHeight();
      };
      const handleWindowResize = () => {
        updateKeyboardHeight();
      };

      window.visualViewport.addEventListener("resize", handleViewportResize);
      window.visualViewport.addEventListener("scroll", handleViewportScroll);
      window.addEventListener("resize", handleWindowResize);

      // Use requestAnimationFrame for smooth, continuous updates
      const updateWithRAF = () => {
        updateKeyboardHeight();
        rafId = requestAnimationFrame(updateWithRAF);
      };
      rafId = requestAnimationFrame(updateWithRAF);

      return () => {
        window.visualViewport.removeEventListener("resize", handleViewportResize);
        window.visualViewport.removeEventListener("scroll", handleViewportScroll);
        window.removeEventListener("resize", handleWindowResize);
        if (rafId) cancelAnimationFrame(rafId);
        if (inputElement) {
          inputElement.removeEventListener("focus", handleInputFocus);
          inputElement.removeEventListener("blur", handleInputBlur);
        }
      };
    } else {
      // Fallback: listen to window resize only
      window.addEventListener("resize", updateKeyboardHeight);

      return () => {
        window.removeEventListener("resize", updateKeyboardHeight);
        if (inputElement) {
          inputElement.removeEventListener("focus", handleInputFocus);
          inputElement.removeEventListener("blur", handleInputBlur);
        }
      };
    }
  }, [isMobile, isMobilePortrait, modalOpen, annotationType]);
  useEffect(() => {
    if (isMobile) {
      let viewport = document.querySelector('meta[name="viewport"]');

      // Create viewport meta tag if it doesn't exist
      if (!viewport) {
        viewport = document.createElement("meta");
        viewport.name = "viewport";
        document.head.appendChild(viewport);
      }

      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      // Add/remove Safari-specific CSS to disable auto-zoom
      const addSafariZoomDisableCSS = () => {
        let safariStyle = document.getElementById("safari-zoom-disable");
        if (!safariStyle) {
          safariStyle = document.createElement("style");
          safariStyle.id = "safari-zoom-disable";
          document.head.appendChild(safariStyle);
        }

        // CSS to disable Safari's automatic zoom on input focus
        safariStyle.textContent = `
          @supports (-webkit-touch-callout: none) {
            input, textarea, select {
              font-size: 16px !important;
              transform: scale(1) !important;
              zoom: 1 !important;
            }
            .MuiInputBase-input {
              font-size: 16px !important;
              transform: scale(1) !important;
              zoom: 1 !important;
            }
            .MuiTextField-root input {
              font-size: 16px !important;
              transform: scale(1) !important;
              zoom: 1 !important;
            }
            .MuiSelect-select {
              font-size: 16px !important;
              transform: scale(1) !important;
              zoom: 1 !important;
            }
            html, body {
              zoom: 1 !important;
              transform: scale(1) !important;
            }
          }
        `;
      };

      const removeSafariZoomDisableCSS = () => {
        const safariStyle = document.getElementById("safari-zoom-disable");
        if (safariStyle) {
          safariStyle.remove();
        }
      };

      // Function to force zoom reset on iOS/Safari
      const forceZoomReset = () => {
        if (isSafari || isIOS) {
          // Multiple approaches to reset zoom

          // Method 1: Reset viewport with forced refresh
          viewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no"
          );

          // Method 2: Force a layout recalculation
          document.body.style.zoom = "1";
          document.documentElement.style.zoom = "1";

          // Method 3: Use transform to reset scale
          document.body.style.transform = "scale(1)";
          document.documentElement.style.transform = "scale(1)";

          // Method 4: Scroll manipulation to reset zoom
          window.scrollTo(0, 0);

          // Method 5: Focus manipulation
          const activeElement = document.activeElement;
          if (activeElement && activeElement.blur) {
            activeElement.blur();
          }

          // Method 6: Force a slight delay then reapply viewport
          setTimeout(() => {
            viewport.setAttribute(
              "content",
              "width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, shrink-to-fit=no, viewport-fit=cover"
            );
          }, 10);

          // Method 7: Trigger a resize event
          setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
          }, 50);
        }
      };

      if (modalOpen || cropperModalOpen || signatureModalOpen) {
        // For mobile text field input, don't restrict overflow in landscape mode
        const isMobileTextField = isMobile && isMobilePortrait && modalOpen && annotationType === "signer_text";
        const shouldRestrictOverflow = !isMobileTextField || !isMobilePortrait;

        // For Safari, set viewport to completely disable zoom
        if (isSafari || isIOS) {
          // Add CSS to prevent auto-zoom
          addSafariZoomDisableCSS();

          // Force zoom reset first
          forceZoomReset();

          // Set aggressive viewport settings for Safari
          viewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, shrink-to-fit=no, viewport-fit=cover"
          );

          // Additional Safari-specific DOM settings
          document.body.style.touchAction = "manipulation";
          document.documentElement.style.webkitUserSelect = "none";
          document.documentElement.style.webkitTouchCallout = "none";
          document.documentElement.style.webkitTapHighlightColor =
            "transparent";

          // Prevent text size adjustment
          document.body.style.webkitTextSizeAdjust = "100%";
          document.documentElement.style.webkitTextSizeAdjust = "100%";

          // Force fixed positioning context - but allow overflow in landscape for text input
          document.body.style.position = "relative";
          document.body.style.overflow = shouldRestrictOverflow ? "hidden" : "auto";

          // Additional zoom prevention
          document.body.style.zoom = "1";
          document.documentElement.style.zoom = "1";
          document.body.style.transform = "scale(1)";
          document.documentElement.style.transform = "scale(1)";
        } else {
          // Non-Safari browsers
          viewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover"
          );
        }

        // Blur any focused elements that might trigger zoom - but don't blur the mobile text input
        const activeElement = document.activeElement;
        const isMobileTextInput = isMobileTextField && mobileInputRef.current &&
          (activeElement === mobileInputRef.current || activeElement === mobileInputRef.current.querySelector('input'));
        if (activeElement && activeElement !== document.body && !isMobileTextInput) {
          activeElement.blur();
          document.body.focus();
        }

        // Scroll to top to reset any zoom - but not if mobile text input is active
        if (!isMobileTextInput) {
          window.scrollTo(0, 0);
        }
      } else {
        // Restore default viewport when modals close
        if (isSafari || isIOS) {
          // Remove Safari-specific CSS
          removeSafariZoomDisableCSS();

          // Restore normal viewport
          viewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1, viewport-fit=cover"
          );

          // Reset Safari-specific styles
          document.body.style.touchAction = "";
          document.documentElement.style.webkitUserSelect = "";
          document.documentElement.style.webkitTouchCallout = "";
          document.documentElement.style.webkitTapHighlightColor = "";
          document.body.style.webkitTextSizeAdjust = "";
          document.documentElement.style.webkitTextSizeAdjust = "";
          document.body.style.position = "";
          document.body.style.overflow = "";
          document.body.style.zoom = "";
          document.documentElement.style.zoom = "";
          document.body.style.transform = "";
          document.documentElement.style.transform = "";
        } else {
          // Non-Safari browsers
          viewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1, viewport-fit=cover"
          );
        }
      }
    }
  }, [modalOpen, cropperModalOpen, signatureModalOpen, isMobile, annotationType, isMobileLandscape, isMobilePortrait]);

  // Handle orientation change to prevent auto-zoom on landscape rotation
  useEffect(() => {
    if (!isMobile) {
      return;
    }

    // Add CSS to prevent zoom on orientation change
    let zoomPreventionStyle = document.getElementById("prevent-orientation-zoom");
    if (!zoomPreventionStyle) {
      zoomPreventionStyle = document.createElement("style");
      zoomPreventionStyle.id = "prevent-orientation-zoom";
      zoomPreventionStyle.textContent = `
        html, body {
          -webkit-text-size-adjust: 100% !important;
          text-size-adjust: 100% !important;
          -ms-text-size-adjust: 100% !important;
        }
        * {
          -webkit-tap-highlight-color: transparent;
        }
      `;
      document.head.appendChild(zoomPreventionStyle);
    }

    let lastOrientation = window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait';

    const resetViewportZoom = () => {
      let viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        viewport = document.createElement("meta");
        viewport.name = "viewport";
        document.head.appendChild(viewport);
      }

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      // Reset viewport to prevent auto-zoom on orientation change
      // This ensures width adapts to new orientation without zooming
      const viewportContent = "width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, viewport-fit=cover";

      // IMMEDIATE reset - don't wait for delays
      if (isSafari || isIOS) {
        // For Safari/iOS: Multiple aggressive resets to prevent auto-zoom
        // Method 1: Remove and re-add viewport immediately
        viewport.removeAttribute("content");
        // Force immediate reflow
        void document.body.offsetHeight;
        // Re-add with correct content
        viewport.setAttribute("content", viewportContent);

        // Method 2: Force zoom reset immediately
        document.body.style.zoom = "1";
        document.documentElement.style.zoom = "1";
        document.body.style.transform = "scale(1)";
        document.documentElement.style.transform = "scale(1)";
        document.body.style.webkitTextSizeAdjust = "100%";
        document.documentElement.style.webkitTextSizeAdjust = "100%";

        // Method 3: Scroll reset
        window.scrollTo(0, 0);

        // Method 4: Multiple re-applications to ensure it sticks
        requestAnimationFrame(() => {
          viewport.setAttribute("content", viewportContent);
          document.body.style.zoom = "1";
          document.documentElement.style.zoom = "1";
          window.scrollTo(0, 0);
        });

        setTimeout(() => {
          viewport.setAttribute("content", viewportContent);
          document.body.style.zoom = "1";
          document.documentElement.style.zoom = "1";
        }, 0);

        setTimeout(() => {
          viewport.setAttribute("content", viewportContent);
          document.body.style.zoom = "1";
          document.documentElement.style.zoom = "1";
        }, 50);

        setTimeout(() => {
          viewport.setAttribute("content", viewportContent);
          document.body.style.zoom = "1";
          document.documentElement.style.zoom = "1";
        }, 150);

        setTimeout(() => {
          viewport.setAttribute("content", viewportContent);
          document.body.style.zoom = "1";
          document.documentElement.style.zoom = "1";
        }, 300);
      } else {
        // For other mobile browsers - immediate reset
        viewport.setAttribute("content", viewportContent);
        document.body.style.zoom = "1";
        document.documentElement.style.zoom = "1";
        document.body.style.webkitTextSizeAdjust = "100%";
        document.documentElement.style.webkitTextSizeAdjust = "100%";
        window.scrollTo(0, 0);

        // Reapply to ensure it sticks
        requestAnimationFrame(() => {
          viewport.setAttribute("content", viewportContent);
          window.scrollTo(0, 0);
        });

        setTimeout(() => {
          viewport.setAttribute("content", viewportContent);
          window.scrollTo(0, 0);
        }, 0);

        setTimeout(() => {
          viewport.setAttribute("content", viewportContent);
        }, 50);

        setTimeout(() => {
          viewport.setAttribute("content", viewportContent);
        }, 150);
      }
    };

    const handleOrientationChange = (e) => {
      // IMMEDIATE reset - don't wait for orientation to fully change
      // Reset viewport BEFORE browser applies auto-zoom
      resetViewportZoom();

      // Also check after orientation settles using requestAnimationFrame for immediate execution
      requestAnimationFrame(() => {
        const currentOrientation = window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait';
        if (currentOrientation !== lastOrientation) {
          lastOrientation = currentOrientation;
          // Reset again after orientation fully changes
          resetViewportZoom();
        }
      });

      // Additional reset after a short delay
      setTimeout(() => {
        resetViewportZoom();
      }, 0);

      setTimeout(() => {
        resetViewportZoom();
      }, 100);
    };

    // Listen for orientation changes - IMMEDIATE handler
    window.addEventListener('orientationchange', handleOrientationChange, { passive: false });

    // Also listen to resize as fallback - but check immediately
    let resizeTimeout;
    const handleResize = () => {
      // Clear any pending timeout
      clearTimeout(resizeTimeout);

      // Check orientation immediately
      const currentOrientation = window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait';
      if (currentOrientation !== lastOrientation) {
        lastOrientation = currentOrientation;
        // Reset immediately
        resetViewportZoom();
      }

      // Also check after resize settles
      resizeTimeout = setTimeout(() => {
        const settledOrientation = window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait';
        if (settledOrientation !== lastOrientation) {
          lastOrientation = settledOrientation;
          resetViewportZoom();
        }
      }, 100);
    };
    window.addEventListener('resize', handleResize, { passive: false });

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      // Don't remove the style tag as it's needed for the entire session
    };
  }, [isMobile, isMobileLandscape]);

  // hashed ids

  useEffect(() => {
    if (isMobile) {
      return;
    }
    const handleResize = debounce(() => {
      // Force re-render of annotations when screen size changes
      if (!isMobile) {
        // Force re-render of annotations when screen size changes
        setAnnotations((prev) => ({ ...prev }));
      }
    }, 100);

    const handleZoom = debounce(() => {
      if (!isMobile) {
        // Detect zoom changes and recalculate
        const currentZoom = window.devicePixelRatio;
        if (Math.abs(currentZoom - (window.lastZoom || 1)) > 0.1) {
          window.lastZoom = currentZoom;

          setAnnotations((prev) => ({ ...prev }));
        }
      }
    }, 100);

    window.addEventListener("resize", handleResize);
    window.addEventListener("wheel", handleZoom, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("wheel", handleZoom);
    };
  }, [isMobile]);
  // Add this right after your state declarations, before any useEffects

  // Replace your existing resize effect with this improved version:

  // Add this function after your existing functions
  // Add this useEffect after your existing ones
  useEffect(() => {
    if (pdfImages.length > 0 && pdfContainerRef.current) {
      // Auto-fit PDF to container on initial load
      // Use a short delay to ensure DOM is ready, then set the calculated scale as default
      const timeoutId = setTimeout(() => {
        const container = pdfContainerRef.current;
        const firstPage = container?.querySelector(".pdf-page-container");
        // Debug logging removed

        if (firstPage && container) {
          const containerWidth = container.offsetWidth;
          const pageWidth = firstPage.scrollWidth;

          // Calculate optimal scale based on container/page ratio
          // Round to nearest valid zoom option to prevent MUI Select warnings
          // Minimum zoom is 100% to prevent positioning issues
          if (pageWidth < containerWidth * 0.7) {
            const calculatedScale = Math.min(1.25, (containerWidth * 0.9) / pageWidth);
            // Round to nearest valid zoom option: 1, 1.25, 1.5, 2 (minimum 100%)
            const validOptions = [1, 1.25, 1.5, 2];
            const nearestOption = validOptions.reduce((prev, curr) =>
              Math.abs(curr - calculatedScale) < Math.abs(prev - calculatedScale) ? curr : prev
            );
            setScale(Math.max(1, nearestOption)); // Ensure minimum is 1
          } else {
            // If PDF fits well, default to 100%
            setScale(1);
          }
        } else {
          // Fallback to 100% if elements not ready
          setScale(1);
        }
      }, 100); // Reduced timeout - DOM should be ready quickly

      return () => clearTimeout(timeoutId);
    }
  }, [pdfImages, isMobile]);

  // Add this enhanced scale effect
  useEffect(() => {
    if (isMobile) {
      return;
    }
    const handleScaleChange = debounce(() => {
      if (!isMobile) {
        // Detect if the PDF container size has changed significantly
        const container = pdfContainerRef.current;
        if (!container) return;

        const newContainerWidth = container.offsetWidth;
        const widthDifference = Math.abs(newContainerWidth - containerWidth);

        // Only trigger recalculation if change is significant (more than 50px)
        if (widthDifference > 50) {
          setContainerWidth(newContainerWidth);

          // Force annotation recalculation
          setTimeout(() => {
            setAnnotations((prev) => {
              const updated = { ...prev };
              updated._lastUpdate = Date.now();
              return updated;
            });
          }, 100); // Small delay to ensure PDF has rendered
        }
      }
    }, 200);

    // Use ResizeObserver for more accurate container size tracking
    const resizeObserver = new ResizeObserver(handleScaleChange);

    if (pdfContainerRef.current) {
      resizeObserver.observe(pdfContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerWidth, isMobile]);

  // Debug logging removed to prevent console flooding

  return (
    <>
      {isLoading && <FullScreenLoader />}

      <Box
        className={mainContainerClass}
        style={{
          touchAction: "none",
        }}
      >
        {/* Add this near the end of your return statement, alongside other modals */}

        <input
          type="file"
          ref={stampInputRef}
          style={{ display: "none" }}
          accept="image/*"
          onChange={handleStampFileSelect} // Remove lastClickPosition parameter
        />
        <FileUpload
          logo={compLogo}
          primaryColorComp={compPrimaryColor}
          imageUrlsCount={imageUrlsCount}
          imageUrls2={imageUrls2}
          all_recipientsData={all_recipientsData}
          fileUserid={fileUserid}
          signerLoginId={signerLoginId}
          currentSignerEmail={currentSignerEmail}
          signer_currentObject={signer_currentObject}
          emailSubject={emailSubject}
          setEmailSubject={setEmailSubject}
          emailMessage={emailMessage}
          setEmailMessage={setEmailMessage}
          originalPdf={originalPdf}
          uniqIdDoc={uniqIdDoc}
          user_email={user_email}
          completed={() => {
            setDocSignerStatusModal(true);
          }}
          receiver={true}
          file_id={file_id}
          annotations={annotations}
          signers={signers}
          setSigners={setSigners}
          user_id={userId}
          file_name={fileName || "Document"}
          // pdfUrl="https://y5f8.c1.e2-7.dev/requiresign-testing-bucket/112316_riazirim22%2F20241106%2F0019_Invoice-2C6D4BDF-0024.pdf" // Add your PDF URL here
          onDownloadOriginal={handleDownloadOriginal}
          isDownloading={isDownloading}
        />
        {/* Unified PDF Container - Responsive for both mobile and desktop */}
        <Grid2
          container
          spacing={0}
          sx={{
            height: { xs: "100vh", sm: "90vh" },
            margin: 0,
            padding: 0,
            marginTop: "0px",
          }}
        >
          {/* Main PDF Viewer */}
          <Grid2
            xs={12}
            className="main-container"
            sx={{
              margin: 0,
              padding: 0,
              width: "100% !important",
              left: "0 !important",
              position: "relative !important",
            }}
          >
            <Grid2
              item
              className={`pdf-container ${isAnnotating ? "custom-cursor" : ""}`}
              ref={pdfContainerRef}
              sx={{
                height: "100%",
                minHeight: { xs: "calc(100vh - 80px)", sm: "100%" }, // Ensure container can grow beyond viewport on mobile
                overflow: { xs: "auto", sm: "auto" }, // Always allow scroll for smoother experience
                display: "flex",
                justifyContent: "center",
                padding: { xs: "5px", sm: "10px" },
                paddingBottom: { xs: "100px", sm: "10px" }, // Increased bottom padding on mobile to prevent overlap with fixed navigation
                margin: 0,
                width: "100%",
                WebkitOverflowScrolling: { xs: "touch", sm: "auto" },
                scrollBehavior: "smooth",
                touchAction: { xs: "pan-x pan-y pinch-zoom", sm: "auto" }, // Allow smooth panning and zooming
                boxSizing: "border-box",
              }}
            >
              {/* Mobile: Enable pinch zoom. Desktop: Normal scroll with centered document */}
              {isMobile ? (
                <TransformWrapper
                  options={{
                    limitToBounds: false,
                    centerOnInit: false,
                    disablePadding: true,
                    minScale: 0.5,
                    maxScale: 5,
                  }}
                  pinch={{ disabled: false }}
                  panning={{
                    disabled: !isZoomed && !(pdfZoomScale > 1.1),
                    velocityDisabled: false,
                    allowLeftClickPan: true,
                    allowRightClickPan: false,
                    allowMiddleClickPan: false,
                  }}
                  wheel={{ disabled: true }}
                  doubleClick={{ disabled: true }}
                  onPanningStart={(ref, event) => {
                    // Prevent panning if user is interacting with text input
                    const target = event.target;
                    if (
                      target.tagName === "INPUT" ||
                      target.tagName === "TEXTAREA" ||
                      target.closest(".MuiTextField-root") ||
                      target.closest(".MuiInputBase-root") ||
                      target.closest('input[type="text"]')
                    ) {
                      return false; // Cancel panning
                    }
                    return true; // Allow panning
                  }}
                  onTransformed={(ref, state) => {
                    const newScale = state.scale;
                    setPdfZoomScale(newScale);
                    const newIsZoomed = newScale > 1.1;

                    // When zoomed out to normal scale (<=1), reset transform position to show first page
                    // The issue is positionY becomes negative from panning, hiding the first page
                    if (newScale <= 1.05 && state.positionY < -10) {
                      // Reset transform position to (0, 0) to show first page from top
                      setTimeout(() => {
                        ref.setTransform(0, 0, 1, 150, "easeOut");

                        // Also reset container scroll as backup
                        const container = pdfContainerRef.current;
                        if (container) {
                          container.scrollTop = 0;
                        }
                      }, 50);
                    }

                    if (newIsZoomed !== isZoomed) {
                      setIsZoomed(newIsZoomed);
                    }
                  }}
                >
                  <TransformComponent
                    wrapperStyle={{
                      width: "100%",
                      height: "auto",
                      overflow: "visible",
                      touchAction: "pan-x pan-y pinch-zoom", // Allow smooth panning and zooming
                    }}
                    contentStyle={{
                      width: "100%",
                      height: "auto",
                      touchAction: "pan-x pan-y pinch-zoom", // Allow smooth panning and zooming
                    }}
                  >
                    <Box
                      className="pdf-content-box"
                      sx={{
                        minWidth:
                          scale > 1
                            ? `${100 * scale}%`
                            : `${calculateImageWidth(scale)}%`,
                        maxWidth: scale > 1 ? `${100 * scale}%` : "100%",
                        position: "relative",
                        alignContent: scale <= 1 ? "center" : "flex-start",
                        justifyContent: scale <= 1 ? "center" : "flex-start",
                        alignItems: scale <= 1 ? "center" : "flex-start",
                        display: "block",
                        paddingTop: { xs: "10px", sm: "0px" }, // Add top padding on mobile to prevent first page cutoff
                        paddingBottom: { xs: "20px", sm: "0px" }, // Add bottom padding on mobile for last page
                        visibility:
                          accessCodeModal === true ||
                            docSignerStatusModal === true ||
                            errorModal === true ||
                            waitforOtherusers === true
                            ? "hidden"
                            : "visible",
                        transformOrigin: "top left",
                      }}
                    >
                      {pdfImages.length > 0 ? (
                        pdfImages.map((image, index) => {
                          const isLastPage = index === pdfImages.length - 1;
                          return (
                            <div
                              key={index}
                              className="pdf-page-container"
                              onMouseEnter={() => handlePageHover(index + 1)}
                              id={`img-${index + 1}`}
                              style={{
                                width: `${calculateImageWidth(scale)}%`,
                                position: "relative",
                                marginBottom: isMobile && isLastPage ? "100px" : undefined, // Extra margin for last page on mobile to prevent overlap
                              }}
                            >
                              {renderImage(image, index)}
                              {renderAnnotations(index + 1)}
                            </div>
                          );
                        })
                      ) : (
                        <Typography>Loading PDF...</Typography>
                      )}
                      {/* Spacer element on mobile to ensure last page is visible above navigation */}
                      {isMobile && pdfImages.length > 0 && (
                        <Box
                          sx={{
                            height: "100px",
                            width: "100%",
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>
                  </TransformComponent>
                </TransformWrapper>
              ) : (
                <Box
                  className="pdf-content-box"
                  sx={{
                    width: "100%",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minHeight: "100%",
                    paddingBottom: { xs: "20px", sm: "0px" },
                    transformOrigin: "top center",
                  }}
                >
                  {pdfImages.length > 0 ? (
                    pdfImages.map((image, index) => {
                      const isLastPage = index === pdfImages.length - 1;
                      const marginBottomValue = isMobile && isLastPage ? "100px" : "20px";
                      return (
                        <div
                          key={index}
                          className="pdf-page-container"
                          onMouseEnter={() => handlePageHover(index + 1)}
                          id={`img-${index + 1}`}
                          style={{
                            width: `${calculateImageWidth(scale)}%`,
                            maxWidth: scale > 1 ? "none" : "100%",
                            minWidth: "300px",
                            position: "relative",
                            margin: "0 auto",
                            marginBottom: marginBottomValue,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <img
                            src={image}
                            alt={`Page ${index + 1}`}
                            className="pdf-image"
                            style={{
                              width: "100%",
                              height: "auto",
                              display: "block",
                              objectFit: "contain",
                              maxHeight: "none",
                            }}
                          />
                          {renderAnnotations(index + 1)}
                        </div>
                      );
                    })
                  ) : (
                    <Typography>Loading PDF...</Typography>
                  )}
                </Box>
              )}
            </Grid2>
          </Grid2>
        </Grid2>

        {signatureModalOpen && (
          <Dialog
            key={`signature-modal-${editingAnnotation?.pageNum}-${editingAnnotation?.index}-${editingAnnotation?.annot?.type}`}
            open={signatureModalOpen}
            onClose={() => {
              setSignatureModalOpen(false);
              setEditingAnnotation(null);
              setInitialBox(false);
            }}
            fullScreen={isMobileOrTablet}
            maxWidth={false}
            slotProps={{
              paper: {
                sx: {
                  width: isMobileOrTablet ? "100vw" : "100vw",
                  maxWidth: isMobileOrTablet ? "100vw" : "1200px",
                  height: isMobileOrTablet ? "100vh!important" : "100vh!important",
                  maxHeight: isMobileOrTablet ? "100vh!important" : "100vh!important",
                  margin: isMobileOrTablet ? 0 : "auto",
                  borderRadius: isMobileOrTablet ? 0 : "8px",
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  overflow: "hidden",
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 20000, // Ensure modal appears above text fields (10000+)
                }
              },
              backdrop: {
                sx: {
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  zIndex: 19999, // Backdrop should be just below modal
                }
              }
            }}
            sx={{
              zIndex: 20000, // Ensure Dialog container is above text fields
              "& .MuiDialog-container": {
                alignItems: isMobileOrTablet ? "stretch" : "center",
                justifyContent: isMobileOrTablet ? "stretch" : "center",
              },
              "& .MuiDialogTitle-root": {
                padding: isMobileOrTablet ? "12px 16px" : "16px 24px",
                width: "100%",
                boxSizing: "border-box",
                flexShrink: 0,
              },
              "& .MuiDialogContent-root": {
                padding: 0,
                width: "100%!important",
                flex: 1,
                overflow: "hidden",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            <SignatureModalContent
              key={`signature-content-${editingAnnotation?.pageNum}-${editingAnnotation?.index}-${editingAnnotation?.annot?.type}`}
              primaryColorComp={compPrimaryColor}
              modalClosingD={() => {
                setSignatureModalOpen(false);
                setEditingAnnotation(null);
                setInitialBox(false);
              }}
              receiverSide={true}
              initialsBox={initialsBox}
              profile={false}
              returnedSignature={handleSignatureComplete}
              modalClose={() => {
                setSignatureModalOpen(false);
                setEditingAnnotation(null);
                setInitialBox(false);
              }}
              user_id_user={user_idSender} // Replace with actual user ID if needed
              user_email={userEmailSender}
            />
          </Dialog>
        )}
      </Box>
      {/* // Add this instead (place it next to your other Dialog components): */}
      <Dialog
        open={cropperModalOpen}
        onClose={() => {
          setCropperModalOpen(false);
          setCropSrc(null);
          setPendingStampAction(null);
          pendingStampActionRef.current = null;
          setStampClickPosition(null);
        }}
        maxWidth={isMobile ? false : "md"}
        fullWidth
        fullScreen={false}
        sx={{
          zIndex: 20000, // Ensure Dialog container is above text fields
          "& .MuiDialog-paper": {
            margin: isMobile ? "20px 0 0 0" : undefined,
            maxWidth: isMobile ? "100%" : undefined,
            maxHeight: isMobile ? "calc(100vh - 20px)" : undefined,
            height: isMobile ? "calc(100vh - 20px)" : undefined,
            width: isMobile ? "100%" : undefined,
            zIndex: 20000, // Ensure modal appears above text fields (10000+)
          },
          "& .MuiBackdrop-root": {
            zIndex: 19999, // Backdrop should be just below modal
          },
        }}
      >
        <DialogTitle
          sx={{
            flexShrink: 0,
            padding: isMobile ? "20px 16px" : "24px 24px 16px 24px",
            fontSize: isMobile ? "20px" : "24px",
            fontWeight: 600,
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fff",
          }}
        >
          Crop Image
          <IconButton
            aria-label="close"
            onClick={() => {
              setCropperModalOpen(false);
              setCropSrc(null);
              setPendingStampAction(null);
              pendingStampActionRef.current = null;
              setStampClickPosition(null);
            }}
            sx={{
              position: "absolute",
              right: isMobile ? 12 : 16,
              top: isMobile ? 16 : 20,
              color: "#666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            padding: isMobile ? "16px" : "24px",
            overflow: "hidden",
            minHeight: 0,
            position: "relative",
            backgroundColor: isMobile ? "#f5f5f5" : "#fff",
          }}
        >
          <ImageCropperModal
            cropSrc={cropSrc}
            isOpen={cropperModalOpen}
            isMobile={isMobile}
            toggle={() => {
              setCropperModalOpen(false);
              setCropSrc(null);
              setPendingStampAction(null);
              pendingStampActionRef.current = null;
              setStampClickPosition(null);
            }}
            onImageCropped={handleCroppedStamp}
          />
        </DialogContent>
      </Dialog>
      {/* // Add the SignersDialog component in your return statement (near other dialogs) */}
      <SignersDialog
        open={signersDialogOpen}
        onClose={() => {
          setSignersDialogOpen(false);
          setSignerToEdit(null); // Clear edit state on close
        }}
        signers={signers}
        file_id={documentId}
        setSigners={setSigners}
        signerToEdit={signerToEdit}
      />
      <AccessCodeDialog
        accessCodeModalData={() => {
          console.log("agasdcfasdfgcfgasc");
          setAccessCodeModal(false);
        }}
        accessCodeModal={waitforOtherusers ? false : accessCodeModal}
        accessCodeSigner={accessCodeSigner}
      />
      <CompletionModal open={docSignerStatusModal} />
      <ErrorModal open={errorModal} />
      <WaitForOthersDialog open={waitforOtherusers} signers={signers} />

      {/* Text Entry Dialog (same as EsignSetupCopy.js) */}
      <TextEntryDialog
        open={textEntryDialogOpen}
        onClose={handleTextEntryCancel}
        onDone={handleTextEntryComplete}
        initialText={textEntryInitialText}
      />

      {/* Date Picker Dialog */}
      <Dialog
        open={datePickerDialogOpen}
        disableEscapeKeyDown={true}
        onClose={(event, reason) => {
          // Prevent closing by clicking outside or pressing escape
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleDatePickerCancel();
        }}
        maxWidth="sm"
        fullWidth={false}
        sx={{
          zIndex: 20000,
          "& .MuiDialog-paper": {
            width: "fit-content",
            maxWidth: "calc(100vw - 32px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            fontSize: "1rem",
          }}
        >
          Select Date
          <IconButton
            size="small"
            onClick={handleDatePickerCancel}
            sx={{ padding: "4px" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "0px 16px 12px 16px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "& .react-datepicker": {
              border: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
            },
            "& .react-datepicker__triangle": {
              display: "none",
            },
            "& .react-datepicker__month-container": {
              width: "100%",
            },
            "& .react-datepicker__day-name, & .react-datepicker__day": {
              width: "2.2rem",
              lineHeight: "2.2rem",
              margin: "0.15rem",
              fontSize: "0.95rem",
            },
            "& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected": {
              borderRadius: "10px",
            },
          }}
        >
          <DatePicker
            selected={datePickerSelectedDate}
            onChange={(date) => {
              if (date && isValid(date)) {
                // Auto-apply selection (easier UX: no extra "Done" click)
                setDatePickerSelectedDate(date);
                handleDatePickerComplete(date);
              }
            }}
            dateFormat="MMM dd, yyyy"
            inline={true}
            minDate={new Date()}
            filterDate={(date) => date >= new Date(new Date().setHours(0, 0, 0, 0))}
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => {
              const months = [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ];
              const currentYear = new Date().getFullYear();
              const years = Array.from({ length: 21 }, (_, i) => currentYear + i);

              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    padding: "10px 10px 6px 10px",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    sx={{ width: 32, height: 32 }}
                  >
                    {"‹"}
                  </IconButton>

                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Select
                      size="small"
                      value={months[date.getMonth()]}
                      onChange={(e) => changeMonth(months.indexOf(e.target.value))}
                      MenuProps={{
                        disablePortal: false,
                        sx: { zIndex: 21001 },
                        PaperProps: { sx: { zIndex: 21001 } },
                      }}
                      sx={{
                        "& .MuiSelect-select": { padding: "6px 10px" },
                        fontSize: "0.95rem",
                      }}
                    >
                      {months.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                    <Select
                      size="small"
                      value={date.getFullYear()}
                      onChange={(e) => changeYear(Number(e.target.value))}
                      MenuProps={{
                        disablePortal: false,
                        sx: { zIndex: 21001 },
                        PaperProps: { sx: { zIndex: 21001 } },
                      }}
                      sx={{
                        "& .MuiSelect-select": { padding: "6px 10px" },
                        fontSize: "0.95rem",
                      }}
                    >
                      {years.map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    sx={{ width: 32, height: 32 }}
                  >
                    {"›"}
                  </IconButton>
                </Box>
              );
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            padding: "8px 16px",
            justifyContent: "space-between",
          }}
        >
          <Button
            size="small"
            onClick={handleDatePickerCancel}
            sx={{ minWidth: "80px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              const today = new Date();
              setDatePickerSelectedDate(today);
              handleDatePickerComplete(today);
            }}
            sx={{ minWidth: "80px", textTransform: "none" }}
          >
            Today
          </Button>
        </DialogActions>
      </Dialog>

      {/* Welcome Modal */}
      <Dialog
        open={welcomeModal}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={true}
        onClose={(event, reason) => {
          // Prevent closing by clicking outside or pressing escape
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            return;
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
            zIndex: 20000, // Ensure modal appears above text fields (10000+)
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "grey", // Simple grey background
            opacity: 1, // Full opacity, no transparency
            zIndex: 19999, // Backdrop should be just below modal
          },
        }}
        sx={{
          zIndex: 20000, // Ensure Dialog container is above text fields
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontSize: "24px",
            fontWeight: "bold",
            color: "rgb(35, 179, 232)",
            paddingBottom: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          {compLogo && compLogo !== "" && (
            <Box
              component="img"
              src={compLogo}
              alt="Company Logo"
              sx={{
                height: "60px",
                width: "auto",
                maxWidth: "200px",
                objectFit: "contain",
                marginBottom: 1,
              }}
            />
          )}
          E-Signature Document
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              textAlign: "center",
              py: 2,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                marginBottom: 3,
                color: "rgba(0, 0, 0, 0.7)",
                lineHeight: 1.6,
              }}
            >
              You have been invited to review and electronically sign this
              document. Please review all fields carefully and complete your
              signature where indicated.
            </Typography>

            {/* KBA Status Messages and Fields */}
            {isKbaRequired && (
              <Box sx={{ mb: 3 }}>
                {/* KBA Success Message */}
                {kbaVerified && kbaVerificationStatus === "passed" && (
                  <Box
                    sx={{
                      mb: 2,
                      padding: 1.5,
                      backgroundColor: "rgba(40, 199, 111, 0.1)",
                      border: "1px solid rgba(40, 199, 111, 0.3)",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#28c76f",
                        fontWeight: 600,
                        flex: 1,
                        textAlign: "center",
                      }}
                    >
                      ✅ KBA Verification Completed Successfully! You can now proceed to sign the document.
                    </Typography>
                  </Box>
                )}

                {/* KBA Failed Message */}
                {kbaVerificationStatus === "failed" && (
                  <Box
                    sx={{
                      mb: 2,
                      padding: 1.5,
                      backgroundColor: "rgba(234, 84, 85, 0.1)",
                      border: "1px solid rgba(234, 84, 85, 0.3)",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#ea5455",
                        fontWeight: 600,
                        textAlign: "center",
                        mb: 1,
                      }}
                    >
                      ❌ KBA Verification Failed. Please try again.
                    </Typography>
                  </Box>
                )}

                {/* KBA Required Message and Input Fields - Show only if not verified */}
                {!kbaVerified && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255)",
                        fontWeight: 800,
                        mb: 2,
                        textAlign: "center",
                        padding: 1.5,
                        backgroundColor: "rgb(0, 149, 255)",
                        borderRadius: 1,
                        border: "1px solid rgba(40, 199, 111, 0.3)",
                      }}
                    >
                      The sender requires you to complete
                      <br /> KBA (Knowledge-Based Authentication)
                      <br />
                      first before signing the document.
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Full Name (First and Last Name Required)"
                        value={kbaFullName}
                        onChange={(e) => setKbaFullName(e.target.value)}
                        required
                        helperText="Please enter both your first and last name"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={kbaEmail}
                        onChange={(e) => setKbaEmail(e.target.value)}
                        required
                        disabled={!!user_email}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: user_email ? "rgba(0, 0, 0, 0.05)" : "white",
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        label="Date of Birth (required for KBA verification)"
                        type="date"
                        value={kbaDateOfBirth}
                        onChange={(e) => setKbaDateOfBirth(e.target.value)}
                        required
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* Terms and Conditions Checkbox */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 1,
                mt: 2,
                mb: 1,
              }}
            >
              {agreeToTerms ? (
                <CheckBoxIcon
                  onClick={() => setAgreeToTerms(!agreeToTerms)}
                  sx={{
                    color: "rgb(35, 179, 232)",
                    cursor: "pointer",
                    fontSize: "20px",
                    mt: 0.2,
                  }}
                />
              ) : (
                <CheckBoxOutlineBlankIcon
                  onClick={() => setAgreeToTerms(!agreeToTerms)}
                  sx={{
                    color: "rgba(0, 0, 0, 0.54)",
                    cursor: "pointer",
                    fontSize: "20px",
                    mt: 0.2,
                  }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(0, 0, 0, 0.7)",
                  textAlign: "left",
                  lineHeight: 1.4,
                  cursor: "pointer",
                }}
                onClick={() => setAgreeToTerms(!agreeToTerms)}
              >
                I agree to the{" "}
                <span
                  style={{
                    color: "rgb(35, 179, 232)",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // If termsConditions is a URL string
                    window.open(termsConditions, "_blank");
                  }}
                >
                  Terms and Conditions
                </span>{" "}
                and consent to electronic signature of this document.
                {" "}
                <ResidencyDisclosure companyName={senderCompanyName} />
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
            paddingBottom: 3,
          }}
        >
          <Button
            variant="contained"
            disabled={
              !agreeToTerms ||
              (isKbaRequired && !kbaVerified && (!kbaFullName || !kbaEmail || !kbaDateOfBirth))
            }
            onClick={async () => {
              if (!agreeToTerms) return;

              // If KBA is required and verified, allow document signing
              if (isKbaRequired && kbaVerified && kbaVerificationStatus === "passed") {
                setWelcomeModal(false);
                return;
              }

              // If KBA is required but not verified, start KBA verification
              if (isKbaRequired && !kbaVerified && kbaFullName && kbaEmail && kbaDateOfBirth) {
                await startKbaVerification();
                return;
              }

              // If KBA is not required, just open document
              if (!isKbaRequired) {
                setWelcomeModal(false);
              }
            }}
            sx={{
              boxShadow: "none",
              backgroundColor:
                agreeToTerms &&
                  (!isKbaRequired || kbaVerified || (kbaFullName && kbaEmail && kbaDateOfBirth))
                  ? "rgb(35, 179, 232)"
                  : "rgba(0, 0, 0, 0.12)",
              color:
                agreeToTerms &&
                  (!isKbaRequired || kbaVerified || (kbaFullName && kbaEmail && kbaDateOfBirth))
                  ? "white"
                  : "rgba(0, 0, 0, 0.26)",
              fontSize: "16px",
              fontWeight: "600",
              borderRadius: "5px",
              textTransform: "none",
              cursor:
                agreeToTerms &&
                  (!isKbaRequired || kbaVerified || (kbaFullName && kbaEmail && kbaDateOfBirth))
                  ? "pointer"
                  : "not-allowed",
              "&:hover": {
                backgroundColor:
                  agreeToTerms &&
                    (!isKbaRequired || kbaVerified || (kbaFullName && kbaEmail && kbaDateOfBirth))
                    ? "rgb(2, 142, 193)"
                    : "rgba(0, 0, 0, 0.12)",
              },
              "&:disabled": {
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {isLoadingKba ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                Starting Verification...
              </>
            ) : isKbaRequired && kbaVerified && kbaVerificationStatus === "passed" ? (
              "Open Document"
            ) : isKbaRequired && kbaVerificationStatus === "failed" ? (
              "Retry KBA Verification"
            ) : isKbaRequired ? (
              "Continue to KBA Verification"
            ) : (
              "Open Document"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* KBA Verification Modal */}
      <Dialog
        open={kbaModalOpen}
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown={true}
        onClose={(event, reason) => {
          // Prevent closing by clicking outside or pressing escape during verification
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            return;
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 0,
            height: "90vh",
            maxHeight: "90vh",
            zIndex: 20000, // Ensure modal appears above text fields (10000+)
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 19999, // Backdrop should be just below modal
          },
        }}
        sx={{
          zIndex: 20000, // Ensure Dialog container is above text fields
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontSize: "20px",
            fontWeight: "bold",
            color: compPrimaryColor || "rgb(35, 179, 232)",
            padding: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ flex: 1 }} />
          <Typography
            component="span"
            sx={{
              flex: 1,
              textAlign: "center",
              fontSize: "20px",
              fontWeight: "bold",
              display: "block"
            }}
          >
            KBA Verification
          </Typography>
          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              onClick={() => {
                setKbaModalOpen(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: 0, height: "100%", position: "relative" }}>
          {isLoadingKba ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "500px",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Starting KBA verification...
              </Typography>
            </Box>
          ) : kbaVerifyUrl && kbaVerifyUrl.trim() !== "" ? (
            <Box sx={{ position: "relative", width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              {/* Info banner if iframe might be blocked */}
              <Box
                sx={{
                  padding: 1.5,
                  backgroundColor: "rgba(33, 150, 243, 0.1)",
                  borderBottom: "1px solid rgba(33, 150, 243, 0.2)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ color: "rgba(33, 150, 243, 0.9)" }}>
                  If the verification page doesn't load below, click the button to open in a new window.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    window.open(kbaVerifyUrl, '_blank', 'noopener,noreferrer');
                    toastAlert("info", "Opening verification in a new window...");
                  }}
                  sx={{
                    ml: 2,
                    textTransform: "none",
                    borderColor: "rgba(33, 150, 243, 0.5)",
                    color: "rgba(33, 150, 243, 0.9)",
                    "&:hover": {
                      borderColor: "rgba(33, 150, 243, 0.8)",
                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                    },
                  }}
                >
                  Open in New Window
                </Button>
              </Box>

              <iframe
                key={kbaVerifyUrl} // Force re-render when URL changes
                src={kbaVerifyUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  minHeight: "500px",
                  flex: 1,
                }}
                title="KBA Verification"
                allow="camera; microphone; fullscreen; autoplay"
                referrerPolicy="no-referrer-when-downgrade"
                loading="eager"
                onLoad={(e) => {
                  console.log("=== KBA IFRAME LOADED ===");
                  console.log("Iframe URL:", kbaVerifyUrl);
                  setIsLoadingKba(false);
                  setIframeLoadAttempted(true);

                  // Check after a delay if iframe actually has content
                  // External sites blocking iframe might show blank or redirect
                  setTimeout(() => {
                    try {
                      const iframe = e.target;
                      // Try to check if iframe content is accessible
                      // This will fail for cross-origin, which is expected
                      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                      if (iframeDoc && iframeDoc.location.href === 'about:blank') {
                        console.warn("Iframe appears to be blank - may be blocked by X-Frame-Options");
                      }
                    } catch (error) {
                      // Cross-origin access denied - this is normal for external sites
                      // The iframe should still display the content even if we can't access it
                      console.log("Cannot access iframe content (cross-origin):", error.message);
                    }
                  }, 2000);
                }}
                onError={(e) => {
                  console.error("=== KBA IFRAME ERROR ===");
                  console.error("Error:", e);
                  console.error("URL:", kbaVerifyUrl);
                  toastAlert("info", "Iframe blocked. Please use 'Open in New Window' button.");
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "500px",
                padding: 3,
                gap: 2,
              }}
            >
              <Typography variant="body1" color="error">
                Failed to load verification page.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please try again or contact support.
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setKbaModalOpen(false);
                  setWelcomeModal(true);
                }}
              >
                Go Back
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile: Floating input bar for text fields */}
      {isMobile && isMobilePortrait && modalOpen && annotationType === "signer_text" && (
        <>
          {/* Backdrop overlay */}
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 19999, // Backdrop should be just below modal
            }}
          />
          {/* Floating input bar - iOS style at top */}
          <Box
            sx={{
              position: "fixed",
              top: "0px",
              left: 0,
              right: 0,
              width: "100%",
              zIndex: 20000, // Ensure floating input appears above text fields (10000+)
              backgroundColor: "#fff",
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              padding: "8px 16px",
              paddingTop: "max(8px, env(safe-area-inset-top))",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "12px",
              transform: "none",
              margin: 0,
              maxWidth: "100%",
              boxSizing: "border-box",
              visibility: "visible",
              opacity: 1,
            }}
          >
            <TextField
              inputRef={mobileInputRef}
              autoFocus
              fullWidth
              variant="outlined"
              value={inputValue}
              onChange={handleDialogInputChange}
              placeholder="Type here..."
              onFocus={(e) => {
                // Ensure keyboard appears when field gets focus - especially important in portrait mode
                const input = e.target;
                if (input && isMobile) {
                  // In portrait mode, ensure input is visible
                  if (isMobilePortrait) {
                    input.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                  // Ensure keyboard is shown
                  input.focus();
                  // Force keyboard on iOS by clicking
                  setTimeout(() => {
                    if (document.activeElement !== input) {
                      input.focus();
                      // Extra focus attempt for portrait mode
                      if (isMobilePortrait) {
                        input.click();
                      }
                    }
                    input.click();
                  }, 0);
                }
              }}
              onClick={(e) => {
                // Ensure keyboard appears when field is clicked - handle portrait mode
                e.stopPropagation();
                if (isMobile && mobileInputRef.current) {
                  const input = mobileInputRef.current;
                  // In portrait mode, ensure input is visible
                  if (isMobilePortrait) {
                    input.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                  input.focus();
                  // Force keyboard on iOS - multiple attempts for portrait mode
                  setTimeout(() => {
                    input.click();
                    input.focus();
                    // Extra attempt for portrait mode
                    if (isMobilePortrait && document.activeElement !== input) {
                      setTimeout(() => {
                        input.focus();
                        input.click();
                      }, 100);
                    }
                  }, 0);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveInput();
                }
              }}
              inputProps={{ maxLength: maxChars }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "16px", // Prevents zoom on iOS
                  "& input": {
                    padding: "12px 16px",
                    fontSize: "16px", // Prevents zoom on iOS
                  },
                },
              }}
            />
            <Button
              sx={{
                fontSize: "16px",
                padding: "8px 20px",
                minWidth: "auto",
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              variant="contained"
              color="primary"
              onClick={handleSaveInput}
            >
              Done
            </Button>
          </Box>
        </>
      )}

      {/* Desktop and non-text fields: Regular Dialog - Only show if NOT mobile text field */}
      {(() => {
        const isMobileTextField = isMobile && isMobilePortrait && annotationType === "signer_text";
        // Don't show Dialog on mobile portrait if annotationType is signer_text (uses floating input bar)
        // Landscape mode shows TextField directly, so no modal needed
        // Only show Dialog when: modalOpen AND (not mobile portrait text field) AND (not mobile with unset annotationType)
        const shouldShowDialog = modalOpen && !isMobileTextField && !(isMobile && !annotationType);
        return shouldShowDialog ? (
          <Dialog
            open={shouldShowDialog}
            fullWidth
            maxWidth={annotationType === "signer_date" ? false : "lg"}
            sx={{
              zIndex: 20000, // Ensure Dialog container is above text fields
              "& .MuiDialog-paper": {
                width: annotationType === "signer_date"
                  ? isMobile ? "95%" : "700px"
                  : "98%", // Make dialog wider for date picker
                height: annotationType === "signer_date"
                  ? isMobile ? "auto" : "520px"
                  : undefined, // Make dialog taller for date picker on desktop only
                maxHeight: annotationType === "signer_date" && isMobile
                  ? "90vh"
                  : undefined, // Limit height on mobile - increased to show full date picker
                maxWidth: annotationType === "signer_date" ? "none" : "none", // Remove max-width for date picker
                margin: isMobile ? "5px" : "10px",
                position: "absolute",
                top: annotationType === "signer_date"
                  ? isMobile ? "5%" : "5%"
                  : "0%", // Move higher on mobile to show full date picker
                transform: "translate(0, 0)",
                display: "flex",
                flexDirection: "column",
                justifyContent: annotationType === "signer_date" && isMobile ? "flex-start" : "center",
                alignItems: "center",
                overflow: "visible",
                zIndex: 20000, // Ensure modal appears above text fields (10000+)
              },
              "& .MuiBackdrop-root": {
                zIndex: 19999, // Backdrop should be just below modal
              },
            }}
            disableEscapeKeyDown={annotationType === "signer_date" || annotationType === "signer_text"}
            onClose={(event, reason) => {
              const isLockedType = annotationType === "signer_date" || annotationType === "signer_text";
              // Prevent closing by clicking outside (and escape) for date/text modals
              if (isLockedType && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
              handleCloseModal();
            }}
          >
            {annotationType !== "signer_text" && (
              <DialogTitle>
                {annotationType === "signer_dropdown"
                  ? "Select an Option"
                  : annotationType === "signer_date"
                    ? "Date"
                    : ""}
              </DialogTitle>
            )}
            <DialogContent
              sx={{
                ...(annotationType === "signer_date" && isMobile && {
                  padding: "8px 16px 16px 16px",
                  maxHeight: "85vh",
                  overflow: "visible",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  "& .react-datepicker": {
                    fontSize: "14px",
                  },
                  "& .react-datepicker__month-container": {
                    width: "100%",
                  },
                  "& .react-datepicker__day": {
                    width: "32px",
                    height: "32px",
                    lineHeight: "32px",
                    fontSize: "14px",
                  },
                  "& .react-datepicker__header": {
                    paddingTop: "8px",
                  },
                }),
              }}
            >
              {annotationType === "signer_dropdown" ? (
                <Select
                  fullWidth
                  value={inputValue}
                  displayEmpty
                  // onChange={(e) => setInputValue(e.target.value)}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setInputValue(newValue);

                    // Update annotation's selectedOption
                    if (activeAnnot) {
                      const { pageNum, index } = activeAnnot;
                      setAnnotations((prev) => {
                        const updatedAnnotations = { ...prev };
                        updatedAnnotations[pageNum][index] = {
                          ...updatedAnnotations[pageNum][index],
                          selectedOption: newValue,
                        };
                        return updatedAnnotations;
                      });
                    }
                  }}
                  renderValue={(selected) => {
                    if (!selected || selected === "") {
                      return (
                        <span style={{ color: "#999" }}>Select an option</span>
                      );
                    }
                    return selected;
                  }}
                  sx={{
                    minWidth: !inputValue || inputValue === "" ? "200px" : "auto",
                    "& .MuiSelect-select": {
                      minWidth: !inputValue || inputValue === "" ? "180px" : "auto",
                      textAlign: "left",
                    },
                  }}
                >
                  {dropdownOptions.length > 0 ? (
                    dropdownOptions.map((option, idx) => (
                      <MenuItem key={idx} value={option}>
                        {option}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No options</MenuItem>
                  )}
                </Select>
              ) : annotationType === "signer_date" ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    ...(isMobile && {
                      "& .react-datepicker": {
                        border: "none",
                        boxShadow: "none",
                        maxWidth: "100%",
                      },
                      "& .react-datepicker__triangle": {
                        display: "none",
                      },
                    }),
                  }}
                >
                  <DatePicker
                    selected={(() => {
                      if (!inputValue || inputValue === "Date" || inputValue === "Date..." || inputValue === "Select a date") {
                        return null;
                      }
                      try {
                        // Try to parse using the expected format
                        const parsedDate = parse(inputValue, "MMM dd, yyyy", new Date());
                        if (isValid(parsedDate)) {
                          return parsedDate;
                        }
                        // Fallback: try direct Date constructor
                        const directDate = new Date(inputValue);
                        return isValid(directDate) ? directDate : null;
                      } catch (error) {
                        return null;
                      }
                    })()}
                    onChange={(date) => {
                      if (date && isValid(date)) {
                        // Auto-apply selection for easier UX
                        setInputValue(format(new Date(date), "MMM dd, yyyy"));
                        handleSaveInput();
                      }
                    }}
                    dateFormat="MMM dd, yyyy"
                    className="custom-datepicker"
                    inline={true} // Use inline date picker
                    minDate={new Date()} // Disable dates before today
                    filterDate={(date) => date >= new Date(new Date().setHours(0, 0, 0, 0))} // Filter out past dates
                    renderCustomHeader={({
                      date,
                      changeYear,
                      changeMonth,
                      decreaseMonth,
                      increaseMonth,
                      prevMonthButtonDisabled,
                      nextMonthButtonDisabled,
                    }) => {
                      const months = [
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                      ];
                      const currentYear = new Date().getFullYear();
                      const years = Array.from({ length: 21 }, (_, i) => currentYear + i);

                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            padding: "10px 10px 6px 10px",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={decreaseMonth}
                            disabled={prevMonthButtonDisabled}
                            sx={{ width: 32, height: 32 }}
                          >
                            {"‹"}
                          </IconButton>

                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Select
                              size="small"
                              value={months[date.getMonth()]}
                              onChange={(e) => changeMonth(months.indexOf(e.target.value))}
                              MenuProps={{
                                disablePortal: false,
                                sx: { zIndex: 21001 },
                                PaperProps: { sx: { zIndex: 21001 } },
                              }}
                              sx={{
                                "& .MuiSelect-select": { padding: "6px 10px" },
                                fontSize: "0.95rem",
                              }}
                            >
                              {months.map((m) => (
                                <MenuItem key={m} value={m}>
                                  {m}
                                </MenuItem>
                              ))}
                            </Select>
                            <Select
                              size="small"
                              value={date.getFullYear()}
                              onChange={(e) => changeYear(Number(e.target.value))}
                              MenuProps={{
                                disablePortal: false,
                                sx: { zIndex: 21001 },
                                PaperProps: { sx: { zIndex: 21001 } },
                              }}
                              sx={{
                                "& .MuiSelect-select": { padding: "6px 10px" },
                                fontSize: "0.95rem",
                              }}
                            >
                              {years.map((y) => (
                                <MenuItem key={y} value={y}>
                                  {y}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={increaseMonth}
                            disabled={nextMonthButtonDisabled}
                            sx={{ width: 32, height: 32 }}
                          >
                            {"›"}
                          </IconButton>
                        </Box>
                      );
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                  }}
                >
                  <TextField
                    fullWidth
                    autoFocus
                    variant="outlined"
                    value={inputValue}
                    onChange={handleDialogInputChange}
                    placeholder="Type here..."
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveInput();
                      }
                    }}
                    inputProps={{ maxLength: maxChars }}
                    sx={{
                      flex: 1,
                    }}
                  />
                  <Button
                    sx={{
                      fontSize: "14px",
                      padding: "8px 20px",
                      minWidth: "auto",
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                    variant="contained"
                    color="primary"
                    onClick={handleSaveInput}
                  >
                    Done
                  </Button>
                </Box>
              )}
            </DialogContent>
            {/* Only show DialogActions for dropdown and date picker, NOT for text fields */}
            {annotationType === "signer_dropdown" || annotationType === "signer_date" ? (
              <DialogActions>
                <Button
                  onClick={handleCloseModal}
                  sx={{
                    fontSize: "12px",
                    padding: "4px 10px",
                    minWidth: "auto",
                    textTransform: "none",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  sx={{
                    fontSize: "12px",
                    padding: "4px 10px",
                    minWidth: "auto",
                    textTransform: "none",
                  }}
                  variant="contained"
                  color="primary"
                  onClick={handleSaveInput}
                >
                  Done
                </Button>
              </DialogActions>
            ) : null}
          </Dialog>
        ) : null;
      })()}
    </>
  );
}

export default EsignReceivingEnd;

